import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import { breakpoints, duration } from '@visious/tokens';
import {
  accumulateSkip,
  chromeVisible,
  clampPlaybackRate,
  decideTap,
  formatRoute,
  keyToCommand,
  loginRoute,
  mediaSessionMetadataFor,
  peerTubeEmbedUrl,
  peerTubeWatchUrl,
  pickPeerTubeMatch,
  resumePosition,
  stageShape,
  stepPlaybackRate,
  toPlaybackPlan,
  youtubeEmbedUrl,
  type InstanceConfig,
  type KeyContext,
  type KeyEventLike,
  type MediaSource_,
  type PeerTubeSearchResult,
  type PlaybackPlan,
  type PlaybackPreferences,
  type PlayerCommand,
  type SkipRun,
  type StoryboardCue,
  type SubtitlePref,
  type TapInput,
  type TapState,
  type VideoDetails,
} from '@visious/core';
import { fetchPeerTubeHlsPlaylistUrl, fetchPeerTubeSearchResults } from '../../api/peertube';
import { fetchStoryboardCues } from '../../api/storyboard';
import { fetchVideoDetails } from '../../api/videos';
import { fetchWatchPageDetails } from '../../api/watchPage';
import {
  useStoredProgress,
  useWatchProgressLookup,
  useWatchProgressRecording,
} from '../../history/useWatchProgress';
import { useWatchedRecording } from '../../history/useWatchedRecording';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import { readPlayerPrefs, writePlayerPrefs, writeVolumePrefs } from '../../platform/playerPrefs';
import { readWatchLayout, writeWatchLayout, type WatchLayoutPrefs } from '../../platform/watchLayout';
import { readSettings, useSettings, writeSettings } from '../../platform/settings';
import { loadShakaPlayer } from '../../player/loadShakaPlayer';
import { isMseSupported } from '../../player/mseSupport';
import { connectYoutubeEmbed, type YoutubeEmbedBridge } from '../../player/youtubeEmbedBridge';
import type { PlayAttempt, PlayerPort, TrackInventory } from '../../player/port';
import {
  audioMenu,
  chaptersOf,
  frameStepSeconds,
  IDLE_TRANSPORT,
  menuWithoutValue,
  mergedAudioMenu,
  qualityMenu,
  shareMenuFor,
  shareOutcomeText,
  shareUrlFor,
  skipFeedbackView,
  speedMenu,
  stageForBootstrapProblem,
  stageForProblem,
  stageForPlan,
  subtitleMenu,
  toRailItems,
  toTransportState,
  toWatchVideo,
  watchOnMenuFor,
  watchOnRowOf,
  watchWithListHref,
  watchWithoutListHref,
  dislikeCounterView,
  PEERTUBE_ROW_ID,
  STABLE_VOLUME_ID,
  SUBTITLE_ROW_PREFIX,
  TIMESTAMP_ROW_ID,
  withAudioSelection,
  withStableVolume,
  withCaptionsToggled,
  withQualitySelection,
  withRateSelection,
  withSubtitleSelection,
  type ActivePlayerSource,
  type MenuId,
  type PeerTubeSwitchState,
  type ShareTarget,
  type PlayerMenu,
  type SkipFeedbackView,
  type ListingItem,
  type TransportState,
  type WatchPlaylistRow,
  type WatchStage,
  type WatchVideo,
} from './model';
import type { MenuModel } from '../../ui/menuModel';
import { navigate } from '../../router/router';
import { useDocumentPlayerKeys } from './keys';
import { useComments, type CommentsBinding } from './useComments';
import { useMediaSession } from './useMediaSession';
import { useWatchPlaylist, type WatchPlaylistBinding } from './useWatchPlaylist';
import { useQueueAdvance } from '../../queue/useQueueAdvance';
import { clearQueue, playQueueEntry, unlinkQueue, useQueue } from '../../queue/store';
import { useQueuePanel } from '../../queue/useQueuePanel';
import { useSponsorBlock, type SponsorBinding } from './useSponsorBlock';
import { useReturnYoutubeDislike } from './useReturnYoutubeDislike';
import { text } from '../../strings';

/**
 * The React binding for the Watch screen: refs, timers, the query and the player's life cycle.
 * Every judgement it makes is delegated to `model.ts` or to core; what is left here is the part that
 * cannot be shared with Android because it is React and the DOM.
 *
 * TanStack Query with **`retry: false`** is not an oversight. §3's retry policy and its backoff
 * live inside `createApiTransport`, and a second retry loop stacked on top would multiply the
 * request count against an instance that is already failing. Query is here for the cache, the
 * request de-duplication and the lifecycle, not for the failure policy.
 */

const POLL_MS = 250;

/** How far the ±10 buttons move. §7's keymap uses the same number through `keyToCommand`. */
export const SKIP_SEC = 10;

/**
 * How long a skip's answer stays on screen. Deliberately *longer* than core's
 * `SKIP_RUN_WINDOW_MS`: while a run can still be joined, what it has counted so far has to still be
 * visible, or a second press at 950 ms would replace nothing with "20 s" after a blink of nothing at
 * all. The margin is the whole reason the two numbers are different.
 *
 * From the token package because the disc's keyframes are the same lifetime seen from the stylesheet
 * (`--vs-duration-skip-feedback`), and a timer and an animation disagreeing about it is a disc that
 * fades out and then sits there invisible.
 */
const SKIP_FEEDBACK_MS = duration.skipFeedback.n;

/**
 * The tail of that lifetime the disc spends fading, so the hold is the rest of it. `slow` rather than a
 * token of its own: this is an ordinary exit transition, and the stylesheet's `@keyframes skipDiscOut`
 * reads the same token — the two have to agree or the element is taken away mid-fade.
 */
const SKIP_FEEDBACK_EXIT_MS = duration.slow.n;

const EMPTY_TRACKS: TrackInventory = { video: [], audio: [], text: [] };

/**
 * What the PeerTube rows read while there is no title and no length to search with. One value rather
 * than a fresh object per render, so the three menus it feeds keep their memoised identity.
 */
const PEERTUBE_NO_INFO: PeerTubeSwitchState = { kind: 'no-info' };

/**
 * The only pointer button the picture answers to: the left one, or a finger, or a pen tip, which all
 * report `0`.
 *
 * A right press is asking the browser for its menu — to copy the video's address, or to save the
 * frame — and pausing underneath that menu answers a question nobody asked. Neither is a middle press
 * anything the player has a meaning for, and the same rule covers both. The bar's own buttons are
 * `<button>`s and were never affected: a click event fires for the primary button alone.
 */
const PRIMARY_POINTER_BUTTON = 0;

export interface WatchHandlers {
  readonly togglePlay: () => void;
  /**
   * Stop, without asking what it was doing — `/embed/ID?end=`'s mark, whose caller has already
   * decided. A toggle there would start a paused video instead.
   */
  readonly pause: () => void;
  readonly seekTo: (seconds: number) => void;
  readonly seekBy: (deltaSec: number) => void;
  readonly toggleMute: () => void;
  readonly setVolume: (level: number) => void;
  readonly toggleDescription: () => void;
  /** Fired by every input path — the keymap, the tap gesture and the buttons all produce commands. */
  readonly command: (command: PlayerCommand) => void;
  readonly key: (event: KeyEventLike, target: KeyContext['target']) => boolean;
  /**
   * §7's double-tap skip, in two halves: the view reports coordinates, core decides the gesture.
   *
   * `button` is the pointer button the event carries, and it is reported rather than filtered because
   * which presses count is a decision, not a measurement — see {@link PRIMARY_POINTER_BUTTON}.
   */
  readonly surfaceDown: (clientX: number, clientY: number, button: number) => void;
  readonly surfaceUp: (
    clientX: number,
    clientY: number,
    xFraction: number,
    pointer: TapInput['pointer'],
    button: number,
  ) => void;
  readonly selectMenuItem: (menu: MenuId, itemId: string) => void;
  readonly openMenu: (menu: MenuId | null) => void;
  readonly toggleFullscreen: () => void;
  /** Drag lifecycle for the scrub bar: the displayed playhead follows the thumb, not the decoder. */
  readonly scrubTo: (seconds: number) => void;
  readonly scrubStart: () => void;
  readonly scrubEnd: () => void;
  /** The gesture was taken away rather than finished. Abandons it — see the handler. */
  readonly scrubCancel: () => void;
  readonly hoverBar: (fraction: number | null) => void;
  /** The pointer moved over the **picture** — restart the idle timer, and it is not on the bar. */
  readonly pointerActivity: () => void;
  /**
   * The pointer moved over the **bar**. Its own handler rather than a flag because the idle timer
   * cannot tell the two apart, and hiding the controls out from under a pointer that has arrived at
   * one is the single case where that timer is measuring the wrong thing (`chromeVisible`).
   */
  readonly chromeActivity: () => void;
  readonly setFocusInside: (inside: boolean) => void;
  /** The page's arrangement — every one of these persists (`platform/watchLayout.ts`). */
  readonly toggleTheater: () => void;
  /**
   * The widen control: fill the box with this picture, 21∶9 in theater and the screen's own shape in
   * fullscreen. Not part of the stored layout, because it is a statement about *this*
   * picture — a cinema upload carrying its bars inside a 16∶9 frame — and the next video is a
   * different picture. It resets with the video for that reason.
   */
  readonly toggleStageCrop: () => void;
  readonly toggleChapters: () => void;
  /**
   * The bar's chapter name was pressed: show the chapter list and bring the playing chapter into
   * view in it. Not a toggle — a press asks to *see* the list, and a second press on a name that is
   * already on screen must not fold the list away under the reader.
   *
   * Fullscreen is left first, since the list lives on the page the picture is covering. The scroll
   * itself belongs to the panel, which is the only thing holding the rows: this raises
   * {@link WatchBinding.revealChapter} and the panel lowers it again with {@link chapterRevealed}
   * once it has scrolled, so a later Show press does not replay a reveal nobody asked for.
   */
  readonly revealChapters: () => void;
  /** The panel, once it has brought the chapter into view. See {@link revealChapters}. */
  readonly chapterRevealed: () => void;
  readonly toggleRecommendations: () => void;
  readonly toggleComments: () => void;
  readonly togglePlaylist: () => void;
  /** Auto-advance. A `set` rather than a toggle: the panel draws a switch, not a button. */
  readonly setPlaylistAutoplay: (on: boolean) => void;
  /** The queue's own, which is a separate stored value (`platform/watchLayout.ts`). */
  readonly setQueueAutoplay: (on: boolean) => void;
  /** Re-request the video. For a failed *fetch*. */
  readonly retry: () => void;
  /**
   * Re-mount the player against the plan we already have. For a failed **load**, which on this
   * instance is a routine event rather than an edge case: `/companion/*` intermittently answers one
   * request with another's body (API reference §3), so a `load()` can be handed MP4 bytes where the
   * manifest should be and reject outright. Nothing about the video is wrong, and re-fetching it
   * would be re-fetching 305 KB to learn the same thing — the right retry is the load.
   */
  readonly retryPlayer: () => void;
}

export interface WatchChrome {
  readonly visible: boolean;
  readonly fullscreen: boolean;
  readonly openMenu: MenuId | null;
  /** `null` while the pointer is off the bar. Drives the tooltip and the storyboard tile. */
  readonly hoverFraction: number | null;
  /** Non-null while a drag is in progress; the bar paints this instead of the decoded position. */
  readonly scrubbingSec: number | null;
  /**
   * Below the `md` step. The bar reads it for the one thing it cannot get from the menus
   * themselves: dropping the total duration, so the time is the playhead alone.
   */
  readonly narrow: boolean;
  readonly quality: PlayerMenu;
  readonly speed: PlayerMenu;
  /** `null` when the video has one audio track or no captions — §7: do not show an empty control. */
  readonly audio: PlayerMenu | null;
  readonly subtitles: PlayerMenu | null;
}

/**
 * Everything the address bar said about this watch page. The whole route rather than
 * `(videoId, startSec)`, because §8's jump-out has to hand `t`, `list` and `index` back out again —
 * a lossless round trip cannot be assembled from the two fields the player happens to need.
 */
export interface WatchTarget {
  readonly videoId: string;
  readonly startSec: number | null;
  readonly playlistId: string | null;
  readonly index: number | null;
}

export interface ShareBinding {
  readonly menu: MenuModel;
  /** Transient — the confirmation ("Link copied") or the refusal, cleared a beat later. */
  readonly feedback: string | null;
  /** A destination copies its link; the timestamp row toggles. The view keeps the menu open for the toggle. */
  readonly onSelect: (itemId: string) => void;
  /**
   * Starts PeerTube's search when the dropdown opens (2026-08-06), the same callback the other two
   * menus carrying that row use — its address is a search result, and this menu is often the first
   * one a reader opens.
   */
  readonly onOpen: () => void;
}

export interface WatchOnBinding {
  readonly menu: MenuModel;
  /**
   * Every row's press, sorted by `watchOnRowOf`. The Open site rows are real `<a>`s the browser
   * follows on its own, so what is left for this handler is the two acts the app makes itself: a
   * player swap, and the timestamp toggle the view keeps the menu open for, like Share's.
   */
  readonly onSelect: (itemId: string) => void;
  /**
   * Starts PeerTube's search when the dropdown opens — the Share menu's own `onOpen` too, since the
   * two read one search. PeerTube is the only destination here that has to be looked up rather than
   * derived from the route.
   */
  readonly onOpen: () => void;
  /**
   * A fallback player has replaced Visious's own — the trigger's "this is the answer to a question
   * you asked" highlight, and the state in which one row of the menu is the way back.
   */
  readonly active: boolean;
}

/** The corner player's two transport steps through its list. `null` where there is no such entry. */
export interface WatchListStep {
  readonly previous: (() => void) | null;
  readonly next: (() => void) | null;
}

/**
 * Auto-advance for whichever list the panel is drawing, resolved to one switch.
 *
 * The two lists keep separate stored values (`platform/watchLayout.ts`) because they ask different
 * questions, and which of them the panel is showing depends on the route — so the choice is made here
 * and the panel is handed a switch rather than a pair of them plus the rule for picking.
 */
export interface WatchListAutoplay {
  readonly on: boolean;
  readonly set: (on: boolean) => void;
}

/**
 * Keeping the list, which is the opposite act on each of the two lists and therefore one control with two
 * faces rather than two controls taking turns.
 *
 * On a **playlist** the list is already kept and what the reader may want is to stop it being the
 * playlist's: {@link WatchListKeep.unlink} hands them the rows as a list of their own. On a **list of
 * their own** the list is the one thing in the app that dies with the tab, so the face is a picker that
 * saves it into a playlist and then reports the id back through {@link WatchListKeep.linkTo}.
 *
 * Both halves are here rather than in `handlers` for `clearList`'s reason: that dispatcher is memoised
 * before the route has said which list this is.
 */
export interface WatchListKeep {
  /**
   * Let go of the playlist and keep whatever of it is loaded, as a list of the reader's own.
   *
   * Two halves in one act, exactly as the panel's Clear has on a linked list: the queue drops its link
   * (`queue/store.ts`'s `unlinkQueue`) **and** the address loses `list` and `index`, or the panel's loader
   * reads the playlist straight back in on the next render and links it again. Nothing is fetched to
   * complete the list first, which is `queueUnlink`'s own rule, and nothing is sent.
   */
  readonly unlink: () => void;
  /**
   * The queue has been saved into that playlist, so the address takes the link up and the ordinary
   * arrival path projects the playlist into the queue. `index` is where the video that is playing landed
   * where the save is what put it there, which saves the read a walk to find it.
   */
  readonly linkTo: (playlistId: string, index: number | null) => void;
  /** `/login?next=<this video>` for the picker's signed-out arm — this page's own, which drops the `t`. */
  readonly signInHref: string;
}

export interface WatchBinding {
  readonly videoId: string;
  readonly stage: WatchStage;
  readonly video: WatchVideo | null;
  /**
   * The video request answered `200` with zero bytes and the watch-page fallback failed too — the
   * one failure where the page around the player still stands (docs/api/video.md §2: the id is
   * well-formed and the instance did not refuse the video, so comments and the playlist acts may
   * still answer). The view reads it to keep the actions row and the comment section rendered
   * under the failure box; every other failure keeps them hidden, for the reason the comment
   * section's own guard states.
   */
  readonly degraded: boolean;
  /** §8's recommendations rail. Inline in the video payload, so it costs no second request. */
  readonly rail: readonly ListingItem[];
  /**
   * §8's jump-out, built from the route rather than from the video — so it is also there when the
   * video is not, which is when a reader most wants it. One menu since the merge (2026-08-02), and
   * since the second one the player swaps live in it too, under their own heading. Share's
   * timestamp row sits under both sections, checked by default here.
   */
  readonly watchOn: WatchOnBinding;
  /** The Share dropdown beside it — copy targets, the timestamp toggle, and the outcome line. */
  readonly share: ShareBinding;
  /**
   * §8's add-to-playlist affordance: where to send a reader with no session — parsed rather than
   * hand-built, so it stays a fixed point of `canonicalHref` (`safeNextHref`). One value rather than
   * a component's worth of state, and not "is there a session" either, which the button reads itself
   * the way every self-contained control here does. It is this page's own href because it drops the
   * `t` (below), which is the only reason the button does not build its own.
   */
  readonly saveToPlaylist: { readonly signInHref: string };
  readonly transport: TransportState;
  /**
   * Whether the loaded picture is taller than it is wide — a Short, or any video shot vertically.
   * `false` until the element's own dimensions have arrived, which is the same "assume landscape"
   * default the 16∶9 skeleton already draws.
   */
  readonly videoPortrait: boolean;
  /**
   * `videoWidth / videoHeight` as a CSS `aspect-ratio` value, or `null` before metadata has loaded —
   * the view writes it as `--video-ar` so the stylesheet can size the mobile player to the real
   * picture instead of assuming 16∶9 ([web-ui.md](../../../../docs/specs/web-ui.md): a media query
   * cannot change a component's own aspect ratio, only a written custom property can).
   */
  readonly videoAspectRatio: string | null;
  /**
   * The shape the **stage** takes, as a plain number the view writes as `--stage-ar` — the ratio of
   * the box itself and, in theater, the multiplier that turns the height cap into the stage's width.
   * `16 / 9` while nothing has been measured, and outside theater for a picture that is merely wide,
   * since there the page owns the width (`stageShape` in core, which decides all three together).
   */
  readonly stageAspectRatio: string;
  /** The picture fills the stage box, cutting off what falls above and below it. */
  readonly stageCrop: boolean;
  /**
   * Whether to draw the widen control at all: a 16∶9 video, in theater or in fullscreen. Those are
   * the two modes whose box can *take* the crop — everywhere else the column's width is fixed and
   * filling a 21∶9 box would only make the video shorter.
   */
  readonly stageCropAvailable: boolean;
  readonly chrome: WatchChrome;
  readonly storyboard: readonly StoryboardCue[];
  /** `null` until something has been attempted. `blocked`/`muted-fallback` are states, not errors. */
  readonly playAttempt: PlayAttempt | null;
  readonly playerError: string | null;
  /**
   * The two Try again buttons' busy faces (2026-08-02). `retrying` is the video *request*
   * being re-fetched; `playerRetrying` is a re-*load* in flight. Separate because their errors are:
   * a fetch failure retries through `retry`, a load failure through `retryPlayer` — and in both
   * cases the error on screen stays up until the attempt resolves, so the button is the only thing
   * that says the press did anything.
   */
  readonly retrying: boolean;
  readonly playerRetrying: boolean;
  readonly descriptionExpanded: boolean;
  /**
   * How the page is arranged: theater, and which side panels are shown. Read once per mount from
   * the store and written back on every toggle, so the next Watch page opens the way this one was
   * left — the "persist locally" half of the toggleable panels.
   */
  readonly layout: WatchLayoutPrefs;
  /**
   * Whether the viewport is at the wide breakpoint — the one where side content can sit beside
   * anything at all (`breakpoints.wide`, the same number the stylesheet's media queries use, kept
   * one by the token test). The view needs it because the *narrow* arrangement changes what is
   * rendered, not merely where: below the description the chapters and recommendations wear the
   * comments header with their own Show/Hide, and the actions row drops its way-back pills.
   */
  readonly wideViewport: boolean;
  /** §8's comment section. Its own hook, because its state is paged and nothing else's is. */
  readonly comments: CommentsBinding;
  /**
   * The side column's list panel — `null` when there is no list, which is the same answer the view needs
   * for "draw the panel at all". Always the queue (`queue/useQueuePanel.ts`), because a `list` in the
   * address makes the queue a window on that playlist; the paged read behind such a list is its own hook,
   * since reading pages is a lifetime concern rather than a render one (`useWatchPlaylist.ts`).
   */
  readonly playlist: WatchPlaylistBinding | null;
  /**
   * Which list that is: a playlist on the instance, or a list of the reader's own. The queue's **link**
   * answers it, so the two are one list with two faces rather than two panels taking turns.
   */
  readonly playlistKind: 'playlist' | 'queue';
  /**
   * Stepping the corner player through that list without leaving the page — its back and forward
   * buttons. Each side is `null` where the list has no entry there, and both are while there is no
   * list at all: the buttons are drawn from this, so nothing to step is nothing to draw.
   */
  readonly listStep: WatchListStep;
  /** Auto-advance for the list the panel is drawing, already resolved — see {@link WatchListAutoplay}. */
  readonly listAutoplay: WatchListAutoplay;
  /**
   * Stop watching the list the panel is drawing, which is one act with a second half for one of the two
   * lists and the reason this is a function rather than a flag the view branches on.
   *
   * The queue goes either way: it lives nowhere but this tab, so that is the whole of what "stop watching
   * this list" can mean about the reader's window on it. A **linked** list also leaves the address —
   * `list` and `index` both — or the panel's loader would read the playlist straight back in. Nothing is
   * sent: the record on the instance is untouched and the video keeps playing.
   *
   * Its own binding field rather than a handler, because `handlers` is memoised before the route has
   * said which list this is.
   */
  readonly clearList: () => void;
  /** The other act on the list as a whole: keep it, which is a different thing on each of the two lists. */
  readonly listKeep: WatchListKeep;
  /** SponsorBlock: the bar's stripes, the chapter badges and the one toast (`useSponsorBlock`). */
  readonly sponsor: SponsorBinding;
  /**
   * A press on the bar's chapter name is waiting for the chapter list to scroll to the chapter it
   * named. Raised by {@link WatchHandlers.revealChapters}, lowered by the panel that acts on it.
   */
  readonly revealChapter: boolean;
  /**
   * Return YouTube Dislike's counter, right of the like one. `null` while the integration is off,
   * the lookup has not answered, or it answered nothing for this video (`useReturnYoutubeDislike`).
   */
  readonly dislikeCountText: string | null;
  readonly dislikeCountTitle: string | null;
  /**
   * The active iframe, or `null` on Visious's own player *and* on PeerTube's "integrated" mode —
   * that leg is a different manifest in the same Shaka player, not a separate surface, so the
   * watch view renders its usual player slot unchanged and never learns which manifest is loaded.
   */
  readonly external: { readonly provider: 'youtube' | 'peertube'; readonly url: string } | null;
  /**
   * The ref for {@link external}'s `<iframe>`. On the YouTube leg it is what the page talks to the
   * embed through (`player/youtubeEmbedBridge.ts`), so every view that draws the frame passes it on.
   */
  readonly externalFrameRef: (frame: HTMLIFrameElement | null) => void;
  /**
   * What a skip is saying right now, or `null` a beat after the last one. The disc and the
   * toast are the same run seen from two distances — `skipFeedbackView` explains which is which.
   */
  readonly skip: SkipFeedbackView | null;
  readonly handlers: WatchHandlers;
  readonly videoRef: RefObject<HTMLVideoElement | null>;
  /** Ours — Shaka's `UITextDisplayer` renders cues into it and our stylesheet owns it (§7). */
  readonly stageRef: RefObject<HTMLDivElement | null>;
}

type WatchLoad =
  | { readonly ok: true; readonly video: VideoDetails; readonly config: InstanceConfig }
  /**
   * `config` is `null` only when §3 resolved no instance at all — a failed *request* still has one.
   * `degraded` marks the zero-byte answer whose page fallback also failed: the one failure the
   * view renders the rest of the page around ({@link WatchBinding.degraded}).
   */
  | {
      readonly ok: false;
      readonly stage: WatchStage;
      readonly config: InstanceConfig | null;
      readonly degraded: boolean;
    };

/**
 * The config travels back with the result — both results — because §3's instance resolution is
 * asynchronous while the jump-out links are built during render. Carrying it on the failure arm too
 * is the point rather than symmetry: a video that would not load is exactly when "watch it on
 * YouTube instead" earns its place, and re-bootstrapping to find that out would be asking §3 a
 * question it has already answered.
 */
async function loadWatch(videoId: string): Promise<WatchLoad> {
  const boot = await getPlatform();
  if (!boot.ok) {
    return { ok: false, stage: stageForBootstrapProblem(boot.problem), config: null, degraded: false };
  }

  const config = boot.platform.config;
  const result = await fetchVideoDetails(boot.platform, videoId);
  if (result.ok) return { ok: true, video: result.value, config };

  /*
   * A `200` with zero bytes is per-video and deterministic (§2, measured on `jNQXAC9IVRw` — 3 of
   * 3 identical requests), so re-asking the same endpoint is not the move: the instance's own
   * `/watch` page is a second source for the same record, and it can carry companion playback for
   * exactly the videos the API has nothing for. A page that answers becomes an ordinary load; one
   * that does not leaves the empty-response stage standing, marked so the view keeps the rest of
   * the page around it.
   */
  if (result.error.kind === 'empty-response') {
    const scraped = await fetchWatchPageDetails(boot.platform, videoId);
    if (scraped.ok) return { ok: true, video: scraped.value, config };
    return { ok: false, stage: stageForProblem(result.error), config, degraded: true };
  }

  return { ok: false, stage: stageForProblem(result.error), config, degraded: false };
}

/**
 * Store the volume pair **after** the port has settled it, and only ever from a user action.
 *
 * Reading it back rather than storing what was asked for is what keeps the two fields honest:
 * `setVolume` un-mutes when the level rises off zero, so "the level the user chose" and "whether
 * sound is on" are decided by the element, not by the caller. The one path that mutes without being
 * asked — `PlayerPort.play()`'s muted-autoplay retry — never reaches this function, which is how
 * §7's "never write the fallback back into the stored level" is enforced structurally.
 */
function persistVolume(player: PlayerPort): void {
  const status = player.status();
  writeVolumePrefs({ volume: status.volume, muted: status.muted });
}

/** Cheap identity for a track list, so the 4 Hz poll only re-renders when the tracks really change. */
function tracksSignature(tracks: TrackInventory): string {
  const keys = (list: readonly { key: string; active: boolean }[]): string =>
    list.map((track) => `${track.key}${track.active ? '*' : ''}`).join('|');
  return `${keys(tracks.video)}/${keys(tracks.audio)}/${keys(tracks.text)}`;
}

export interface WatchScreenOptions {
  /**
   * `false` for the /embed screen (2026-08-02): it renders no comment section, and a section
   * that is not rendered must not be fetched — `useComments` only answers Show/Hide, so without
   * this the layout prefs of a page the embed never draws would decide whether a request goes out.
   */
  readonly comments?: boolean;
  /**
   * `true` for the /embed screen (2026-09-14): its Watch on links open a tab of their own, since
   * following one in place would put the destination inside the embedder's frame and take their
   * page with it. The links themselves are the ordinary watch-page ones either way.
   */
  readonly jumpOutNewTab?: boolean;
  /**
   * `false` for /embed: the route carries no `list` there anyway, but a flag rather than a
   * coincidence — an embedded player is one video inside somebody's page, and a side panel that
   * navigated the iframe to the next entry is the last thing an embed should grow.
   */
  readonly playlist?: boolean;
  /**
   * The player is the minimized one in the corner rather than a page. Unlike the two above this changes
   * over the hook's life — it is what a navigation away from /watch means — and it turns off exactly two
   * behaviours, both of which belong to *a page the reader is looking at*:
   *
   * - **the document-level keymap**, which would otherwise take `←`/`→`/`k` away from the feed grid
   *   the reader is actually navigating (`keys.ts` explains why that listener is on the document at
   *   all, and this is the one case its three declinations do not cover: the presses are unclaimed
   *   and outside a text field, so core answers them);
   * - **the navigation an advance makes**. The list still moves on in the corner, by moving the queue's
   *   cursor, which is a change of state the box follows; a corner player that walked the whole app to
   *   the next video would be the opposite of minimized.
   */
  readonly minimized?: boolean;
  /**
   * Whether this session may start itself, overriding the Playback tab's "Start playing on arrival".
   *
   * `undefined` — every session that came from a watch route — leaves the setting in charge, which is
   * what it has always meant: the reader opened a page and said whether pages start playing. The
   * override exists for the queue (`playback/PlaybackHost.tsx`), where the same question has two
   * different right answers depending on how the session began: a queue created from a grid honours
   * the corner player's own switch, while the entry an advance landed on plays because the one before
   * it was playing.
   */
  readonly autoplay?: boolean;
  /**
   * Start silent whatever level the reader last left — `/embed/ID?mute=1`. It sets the player's own
   * mute flag rather than the stored preference, so unmuting from the bar works normally and nothing
   * about this page is written back to the reader's settings.
   */
  readonly startMuted?: boolean;
  /**
   * Stand in for the reader's stored subtitle default, for this session only — an embed's
   * `cc_lang_pref` and `cc_load_policy` together. Read once at mount like the other playback
   * defaults, so the menu keeps the last word.
   */
  readonly subtitle?: SubtitlePref;
  /**
   * `false` unbinds the keymap outright — `/embed/ID?disablekb=1`. Both listeners go through
   * `handlers.key`, so refusing there is refusing everywhere: the surface's own presses and the
   * document-level ones alike.
   */
  readonly keyboard?: boolean;
}

export function useWatchScreen(target: WatchTarget, options: WatchScreenOptions = {}): WatchBinding {
  const { videoId, startSec } = target;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<PlayerPort | null>(null);
  /**
   * The previous player's `destroy()`, still in flight — a cleanup cannot await, so the *next*
   * mount does. Without this, Try again deadlocked (2026-08-02, measured): `destroy()` detaches
   * the media element while the fresh player's `attach()` claims it, and the attach never
   * resolves — which read as a retry that ignored the press. Stored through {@link swallow}: a
   * teardown that *rejects* must not poison every later mount's `await`.
   */
  const playerTeardownRef = useRef<Promise<void> | null>(null);

  const [transport, setTransport] = useState<TransportState>(IDLE_TRANSPORT);
  /**
   * The loaded picture's own shape — a Short's 1080×1920 as readily as a dub's 1920×1080 — read off
   * the `<video>` element rather than any manifest field, because the no-MSE arm has no `PlayerPort`
   * to ask and a track's declared height carries no width to pair it with. `null` until metadata has
   * actually arrived, which is what keeps the loading skeleton at its usual 16∶9 guess rather than
   * flashing a shape nobody has measured yet.
   */
  const [videoDimensions, setVideoDimensions] = useState<{
    readonly width: number;
    readonly height: number;
  } | null>(null);
  const [tracks, setTracks] = useState<TrackInventory>(EMPTY_TRACKS);
  const tracksKeyRef = useRef('');
  const [playAttempt, setPlayAttempt] = useState<PlayAttempt | null>(null);
  const [playerError, setPlayerError] = useState<string | null>(null);
  /** True from `retryPlayer` until that load settles — the Try again button's busy face. */
  const [playerRetrying, setPlayerRetrying] = useState(false);
  // A load error belongs to *its* video: since success is what clears it now (2026-08-02),
  // navigating from a failed video to a working one must not carry the failure across. Adjusted
  // during render (React's documented pattern, `ConfirmButton`'s precedent), not in an effect.
  const [errorVideoId, setErrorVideoId] = useState(videoId);
  if (errorVideoId !== videoId) {
    setErrorVideoId(videoId);
    setPlayerError(null);
    setPlayerRetrying(false);
  }

  /*
   * The player escape hatch (2026-08-03): a fresh video always starts on Visious, exactly
   * like `errorVideoId` above resets a failure to the video it belongs to — adjusted during render
   * rather than in an effect, so no frame shows the new video still wearing the old one's switch.
   */
  const [switchVideoId, setSwitchVideoId] = useState(videoId);
  const [activeSource, setActiveSource] = useState<ActivePlayerSource>('visious');
  const [external, setExternal] = useState<{
    readonly provider: 'youtube' | 'peertube';
    readonly url: string;
  } | null>(null);
  /** The matched PeerTube video's HLS manifest — set only for the "integrated" mode. */
  const [peerTubeIntegratedUrl, setPeerTubeIntegratedUrl] = useState<string | null>(null);
  /**
   * Where the *next* load of the effective manifest should resume — captured at the moment of a
   * switch (either direction), because "always carry the current position over" (decided with the
   * reader) means neither leg knows the other's timeline: switching away pauses Visious's own
   * player in place, and switching back reloads it from wherever the foreign player was, not from
   * the original `t=`.
   */
  const [switchSeek, setSwitchSeek] = useState<number | null>(null);
  const [peerTubeSwitchState, setPeerTubeSwitchState] = useState<PeerTubeSwitchState>({ kind: 'idle' });
  /** The cached hit behind a `found` state — what the click handler acts on without searching again. */
  const [peerTubeMatch, setPeerTubeMatch] = useState<PeerTubeSearchResult | null>(null);
  if (switchVideoId !== videoId) {
    setSwitchVideoId(videoId);
    setActiveSource('visious');
    setExternal(null);
    setPeerTubeIntegratedUrl(null);
    setSwitchSeek(null);
    setPeerTubeSwitchState({ kind: 'idle' });
    setPeerTubeMatch(null);
  }

  /*
   * The YouTube leg's message bridge (2026-10-09). YouTube keeps its own chrome; what the bridge adds
   * is that the rest of the page follows the embed instead of the paused player hidden under it — the
   * transport poll reads it (`sampleTransport`), so the playing chapter, the resume position, the
   * history mirror and the Share timestamp all keep moving, and the dispatcher sends it the commands
   * it can take (`runCommand`), so a chapter, a timestamp or a SponsorBlock skip seeks the embed.
   *
   * A ref rather than state, like `playerRef`: both readers are callbacks, and the 4 Hz poll is what
   * turns the bridge's state into renders. Built per frame *and* per address, since a new `src` is a
   * new player behind the same element.
   */
  const [externalFrame, setExternalFrame] = useState<HTMLIFrameElement | null>(null);
  const youtubeRef = useRef<YoutubeEmbedBridge | null>(null);
  const youtubeUrl = external?.provider === 'youtube' ? external.url : null;
  useEffect(() => {
    if (youtubeUrl === null || externalFrame === null) return;
    const bridge = connectYoutubeEmbed(externalFrame);
    youtubeRef.current = bridge;
    return () => {
      bridge.destroy();
      if (youtubeRef.current === bridge) youtubeRef.current = null;
    };
  }, [externalFrame, youtubeUrl]);

  /** Bumped by `retryPlayer`; part of the mount effect's deps, so bumping it re-mounts. */
  const [playerAttempt, setPlayerAttempt] = useState(0);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [layout, setLayout] = useState<WatchLayoutPrefs>(readWatchLayout);
  /*
   * The widen control's own state, and the one piece of the stage's shape a reader sets by hand. It
   * is deliberately **not** in the stored layout: the rest of that store is how the page is arranged,
   * which holds from one video to the next, while this says "this picture is wider than the frame it
   * arrived in" — true of the cinema upload in front of the reader and of nothing else. So it resets
   * with the video, adjusted during render like `errorVideoId` above rather than in an effect, so no
   * frame shows a new video wearing the last one's crop.
   */
  const [stageCrop, setStageCrop] = useState(false);
  const [cropVideoId, setCropVideoId] = useState(videoId);
  if (cropVideoId !== videoId) {
    setCropVideoId(videoId);
    setStageCrop(false);
  }
  const wideViewport = useWideViewport();
  /**
   * Below the `md` step. The bar renders *differently* there rather than smaller: no values
   * beside the icons, no total duration, no skip buttons, and audio and subtitles behind one control —
   * every one of them a decision about what the line can hold, which is why it is a flag here and not
   * four media queries scattered through a stylesheet that cannot change what a menu contains.
   */
  const narrowViewport = useNarrowViewport();

  // Chrome state.
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [hoverFraction, setHoverFraction] = useState<number | null>(null);
  const [scrubbingSec, setScrubbingSec] = useState<number | null>(null);
  const [focusInside, setFocusInside] = useState(false);
  const [pointerOverChrome, setPointerOverChrome] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  /**
   * What shape to give the stage — the box's ratio, whether the picture fills it, and whether the
   * widen control is on offer. All three come out of one core call, because a video wider than 16∶9
   * and a 16∶9 one carrying its bars inside the frame are the same question answered two ways.
   *
   * Both modes are inputs, and the crop needs **either** of them: those are the two places the
   * picture's height is what is free to move. On the ordinary page the column's width is fixed, so a
   * crop could only make the video shorter, which is not a zoom — hence no crop there, and no
   * control offering one. It sits below the fullscreen state because it reads it.
   */
  const stageBox = stageShape({
    videoWidth: videoDimensions?.width ?? 0,
    videoHeight: videoDimensions?.height ?? 0,
    theater: layout.theater,
    fullscreen,
    cropRequested: stageCrop,
  });
  const [pointerIdleMs, setPointerIdleMs] = useState(0);
  const lastPointerMsRef = useRef(0);
  const tapStateRef = useRef<TapState | null>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  /**
   * The skip currently being answered on screen, and the timer that takes it away again. The
   * run itself is a ref as well as state: the *next* press has to read the total synchronously to add
   * to it, and a press arriving before React has re-rendered would otherwise restart the count at 10
   * — which is precisely the run of quick presses the feature exists for.
   */
  const [skipFeedback, setSkipFeedback] = useState<{
    readonly run: SkipRun;
    readonly origin: 'gesture' | 'control';
    /** Set by the hold timer, cleared by the next press — see `noteSkip`. */
    readonly leaving: boolean;
  } | null>(null);
  const skipRunRef = useRef<SkipRun | null>(null);
  const skipTimerRef = useRef<number | null>(null);
  const skipFadeTimerRef = useRef<number | null>(null);
  /** The caption language `C` returns to, so toggling twice is a round trip and not a reset. */
  const lastSubtitleCodeRef = useRef<string | null>(null);

  /**
   * "3 weeks ago" needs a *now*, and reading the clock during render is impure — the same render
   * would produce a different string on every re-paint, which is exactly what React's purity rule
   * catches. Sampled once per mount instead: a page left open long enough for this to drift is a
   * page whose "3 weeks ago" was never going to tick over anyway.
   */
  const [nowEpochSec] = useState(() => Math.floor(systemClock.nowEpochMs() / 1000));

  /**
   * The stored playback defaults — all four of them.
   *
   * The note this replaces said quality, audio and subtitles deliberately did *not* persist, because
   * "1080p" and "German" are resolved against whatever tracks a given manifest turned out to have and so
   * mean something different on the next video. That reasoning is still right about the **resolution**,
   * and it is exactly why they are safe to persist: `decideVideoTrack`/`decideAudioTrack`/`decideSubtitle`
   * already answer "the nearest one" or "unavailable" per video, and the menus report which happened. A
   * preference that cannot be met is a preference, not a bug — what was missing was a *place to set it*,
   * which is what §8's Settings page is.
   *
   * Read once per mount rather than subscribed: a settings change mid-video must not re-`load()` the
   * player. It takes effect on the next video, which is what "default" means.
   */
  const [preferences, setPreferences] = useState<PlaybackPreferences>(() => {
    const stored = readSettings();
    const playback = {
      ...stored.playback,
      playbackRate: clampPlaybackRate(stored.playback.playbackRate),
      // An embed's caption parameters speak for the page they are on, so they stand in for the
      // reader's own default here — and are never written back to it.
      ...(options.subtitle === undefined ? {} : { subtitle: options.subtitle }),
    };
    // The stable-volume setting enters as the preference's `drc` — the stored document never carries
    // it (`normalizeAudioPref`), so this stamp is the one place the setting reaches the player.
    return stored.useStableVolume ? withStableVolume(playback, true) : playback;
  });
  /** Read once per mount, for the same reason. Autoplay and resume are what the player reads from it. */
  const [playbackSettings] = useState(() => readSettings());
  /** A primitive rather than `options.keyboard`, so the handler memo below is not rebuilt per render. */
  const keyboardEnabled = options.keyboard !== false;
  /**
   * 4b's stored playhead for *this* video, read once (`useStoredProgress`) — never subscribed.
   *
   * The resume position becomes the player's start position, and the start position is in the mount
   * effect's dependencies. So a value that changed would re-`load()` the stream: the recorder below
   * writes every five seconds, which would restart the video twelve times a minute.
   */
  const storedProgress = useStoredProgress(videoId);
  const resumeSec = useMemo(
    () =>
      resumePosition({
        mark: storedProgress,
        // A `t` in the address bar wins outright — see `resumePosition`. That covers a shared link, a
        // chapter, a comment's timecode and a clip, all of which are somebody's deliberate address.
        requestedStartSec: startSec,
        enabled: playbackSettings.resumePlayback,
      }),
    [playbackSettings.resumePlayback, startSec, storedProgress],
  );
  /** Live, for the one setting that must not wait for the next video — see the recorder below. */
  const settings = useSettings();
  /**
   * A PeerTube search already run is cached (`peerTubeSwitchState`) until the video changes (above)
   * or until the settings that decided its answer change: the instance asked and the search target
   * both pick which server may answer, so a stale found/unavailable from before an edit would show a
   * match nothing just verified, or a miss nothing just re-tried.
   */
  const peerTubeSearchKey = `${settings.peerTube.apiUrl ?? ''} ${settings.peerTube.searchTarget}`;
  const [switchPeerTubeSearchKey, setSwitchPeerTubeSearchKey] = useState(peerTubeSearchKey);
  if (switchPeerTubeSearchKey !== peerTubeSearchKey) {
    setSwitchPeerTubeSearchKey(peerTubeSearchKey);
    setPeerTubeSwitchState({ kind: 'idle' });
    setPeerTubeMatch(null);
  }
  /**
   * Read by the mount effect and by every command, neither of which may re-run when a preference
   * changes: a menu selection must re-`apply()`, never re-`load()`. Kept in step with the state
   * inside `applyPreferences` rather than in an effect, so a command issued in the same tick as a
   * selection already sees the new value.
   */
  const preferencesRef = useRef(preferences);
  /** Mirrors `scrubbingSec` so the drag can be committed from a callback that has no state closure. */
  const scrubbingRef = useRef<number | null>(null);
  /**
   * Whether a **pointer** currently owns the bar. Deliberately separate from `scrubbingRef`, which is
   * only *what the bar paints*: the browser sends one more value change after the gesture is over, and
   * telling the two apart is what stopped that echo from seeking (see `scrubEnd`).
   */
  const draggingRef = useRef(false);

  const query = useQuery({
    queryKey: ['video', videoId],
    queryFn: () => loadWatch(videoId),
    // The transport owns retry and backoff (§3/R6). See the note above.
    retry: false,
    // Signed media URLs live ~5h45m and the manifest rotates them inside ~26 minutes (0f), so a
    // cached plan older than a few minutes is worth re-fetching rather than handing to the player.
    staleTime: 5 * 60 * 1000,
  });

  const details = query.data?.ok === true ? query.data.video : null;

  const plan: PlaybackPlan | null = useMemo(() => {
    if (details === null) return null;
    // `mseSupported` is a fact about the engine, so it is measured here and passed in — core does
    // not get to ask the browser anything (R1).
    return toPlaybackPlan(details, { mseSupported: isMseSupported() });
  }, [details]);

  const chapters = useMemo(() => chaptersOf(plan), [plan]);

  const stage: WatchStage = useMemo(() => {
    if (query.isPending) return { kind: 'loading' };
    /*
     * The plan first, then the thrown error, then the stored failure (2026-08-02). The order
     * is the whole rule "an error only leaves once a load succeeds": a successful payload always
     * plays — a *background* refetch that throws must not take down the video already playing —
     * while on the failure side the thrown error outranks `query.data`'s stage because a throw is
     * always the fresher attempt (a queryFn that *returns* its failure clears the thrown one).
     */
    if (query.error !== null && plan === null) {
      return {
        kind: 'problem',
        title: text().watch.page.playerFailedTitle,
        detail: query.error.message,
        // `loadWatch` returns every classified failure as a stage, so a *throw* is the one thing
        // nothing graded. Offering the button is the honest answer to an unknown.
        retryable: true,
      };
    }
    if (query.data?.ok === false) return query.data.stage;
    if (plan === null) return { kind: 'loading' };
    return stageForPlan(plan, {
      /*
       * 4b — the resume position enters as *the start position the route did not carry*, which is what
       * makes live correct for free: `stageForPlan` already refuses a start position on a live plan,
       * because the seekable window has moved by the time anything lands (§7, measured in 0f).
       */
      startSec: startSec ?? resumeSec,
      mseSupported: isMseSupported(),
      nowEpochSec,
    });
  }, [nowEpochSec, plan, query.data, query.error, query.isPending, resumeSec, startSec]);

  const video = useMemo(
    () => (details === null ? null : toWatchVideo(details, chapters, nowEpochSec)),
    [chapters, details, nowEpochSec],
  );

  // §8's rail. `recommendedVideos[]` arrives inside the video payload 1a already parses, so this is
  // a derivation and not a request — which is the whole reason 1f is the smallest slice in phase 1.
  //
  // The *lookup* here is subscribed, unlike this screen's own stored position: a rail tile is a row
  // like any other, and its bar should appear the moment the reader comes back from watching it.
  const railProgress = useWatchProgressLookup();
  const rail = useMemo(
    () => (details === null ? [] : toRailItems(details.recommended, nowEpochSec, railProgress)),
    [details, nowEpochSec, railProgress],
  );

  // Present on both arms of `WatchLoad`, so the jump-out survives a video that did not load. Only
  // the request being still in flight leaves it `null`, and then there is nothing on screen yet.
  const config = query.data?.config ?? null;

  /**
   * Whether a PeerTube search can be attempted at all: it matches on the title *and* on a length
   * within a second, so both have to be known before a request is worth sending. A failed load has
   * neither, and a live stream reports `lengthSeconds: 0` — a length no upload can be within a
   * second of, so the search would spend a request to answer "no video found" with something the
   * page already knows.
   */
  const peerTubeSearchable =
    video !== null && video.title.trim() !== '' && details !== null && details.lengthSeconds > 0;

  /**
   * What the three PeerTube rows read. Derived rather than stored, so the stored state stays `idle`
   * while there is nothing to search with and the first dropdown opened after the details arrive
   * still starts the search.
   */
  const peerTubeRowState: PeerTubeSwitchState = peerTubeSearchable
    ? peerTubeSwitchState
    : PEERTUBE_NO_INFO;

  /*
   * The Share menu (2026-08-02). The timestamp toggle and the copy outcome are this hook's
   * state rather than the view's because the *copy itself* is a judgement over model state — which
   * URL, whether the playhead rides along — and the view only forwards item ids. The clipboard call
   * lives here for the same reason the tab title does: it is a DOM side effect on the R13 line's
   * web side.
   */
  const [shareTimestamp, setShareTimestamp] = useState(false);
  const [shareOutcome, setShareOutcome] = useState<'copied' | 'failed' | null>(null);
  const shareOutcomeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (shareOutcomeTimer.current !== null) clearTimeout(shareOutcomeTimer.current);
    },
    [],
  );

  /*
   * `next` is the *watch route*, so approving the grant comes back to this video — with `t` deliberately
   * dropped. A sign-in is a top-level navigation to the instance and back, which takes long enough that
   * resuming at the second the reader left is a worse answer than starting where the URL says.
   */
  const signInHref = useMemo(
    () =>
      formatRoute(
        loginRoute({
          kind: 'watch',
          videoId: target.videoId,
          startSec: null,
          playlistId: target.playlistId,
          index: target.index,
        }),
      ),
    [target.index, target.playlistId, target.videoId],
  );

  // The tab title is a DOM side effect, so it belongs on this side of the R13 line.
  useEffect(() => {
    if (video === null) return;
    document.title = `${video.title} · Visious`;
    return () => {
      document.title = 'Visious';
    };
  }, [video]);

  const invidiousSource = stage.kind === 'player' ? stage.source : null;
  const invidiousStartAt = stage.kind === 'player' ? stage.startSec : null;
  const live = stage.kind === 'player' && stage.live;

  /*
   * The *effective* source the player actually loads (2026-08-03) — Visious's own, unless the
   * reader switched to PeerTube's "integrated" mode, in which case the matched video's HLS master
   * stands in for it. No new `PlayerPort` method: `MediaSource_` is already just data, so a foreign
   * manifest is a value of the same shape rather than a different load path. `subtitles: []` and
   * `storyboardVttUrl: null` are honest rather than omitted — PeerTube sideloads neither today, and
   * both menus already render "nothing" correctly for an empty list. `expiresAtEpochSec: null`
   * because nothing about this URL expires the way a signed googlevideo one does.
   */
  const source: MediaSource_ | null = useMemo(
    () =>
      peerTubeIntegratedUrl !== null
        ? {
            manifestUrl: peerTubeIntegratedUrl,
            manifestKind: 'hls' as const,
            fallbackProgressiveUrl: null,
            subtitles: [],
            storyboardVttUrl: null,
            expiresAtEpochSec: null,
          }
        : invidiousSource,
    [invidiousSource, peerTubeIntegratedUrl],
  );
  const manifestUrl = source?.manifestUrl ?? null;
  /**
   * `switchSeek` overrides the plan's own `t=`/resume position for exactly as long as it is set —
   * which is "since the last switch", the same "resume this manifest from here" contract a manual
   * Try again already has for the plain Invidious case (`invidiousStartAt` is equally a one-time
   * value re-read on every reload, not a live position).
   */
  const startAt =
    peerTubeIntegratedUrl !== null ? (switchSeek ?? 0) : (switchSeek ?? invidiousStartAt);

  /*
   * The jump-out's timestamp toggle starts **checked** — the opposite of Share's — because following
   * a link out is "keep watching over there", where the playhead is the point, while a copied link
   * is for someone who has not watched anything yet. While nothing is playing the position falls
   * back to the route's own `t`, so the failure states' links still land where the URL said — the
   * lossless round trip those states were built for.
   */
  const [jumpOutTimestamp, setJumpOutTimestamp] = useState(true);
  const jumpOutNewTab = options.jumpOutNewTab ?? false;
  const watchOnMenu = useMemo(
    () =>
      watchOnMenuFor(
        config,
        {
          kind: 'watch',
          videoId: target.videoId,
          startSec: target.startSec,
          playlistId: target.playlistId,
          index: target.index,
        },
        {
          newTab: jumpOutNewTab,
          includeTimestamp: jumpOutTimestamp,
          positionSec: stage.kind === 'player' ? transport.currentSec : (target.startSec ?? 0),
          live,
          // The same search the Share menu reads, so whichever dropdown opens first pays for it.
          peerTube: settings.peerTube.enabled
            ? { state: peerTubeRowState, match: peerTubeMatch }
            : null,
          youtubeSwitch: settings.youtubeEmbed.enabled,
          activeSource,
        },
      ),
    [
      activeSource,
      config,
      jumpOutNewTab,
      jumpOutTimestamp,
      live,
      peerTubeMatch,
      peerTubeRowState,
      settings.peerTube.enabled,
      settings.youtubeEmbed.enabled,
      stage.kind,
      target.index,
      target.playlistId,
      target.startSec,
      target.videoId,
      transport.currentSec,
    ],
  );

  // The menu re-derives on every transport tick while playing — that is the timestamp row's label
  // keeping honest about what it would copy, and the object is a handful of strings.
  const shareMenu = useMemo(
    () =>
      shareMenuFor(config, {
        includeTimestamp: shareTimestamp,
        positionSec: transport.currentSec,
        live,
        // The third dropdown reading the one search (2026-08-06) — see `jumpOutMenu` above.
        peerTube: settings.peerTube.enabled
          ? { state: peerTubeRowState, match: peerTubeMatch }
          : null,
      }),
    [
      config,
      live,
      peerTubeMatch,
      peerTubeRowState,
      settings.peerTube.enabled,
      shareTimestamp,
      transport.currentSec,
    ],
  );

  const shareSelect = useCallback(
    (itemId: string) => {
      if (itemId === TIMESTAMP_ROW_ID) {
        setShareTimestamp((value) => !value);
        return;
      }
      const startSec =
        shareTimestamp && !live ? Math.max(0, Math.floor(transport.currentSec)) : null;
      /*
       * PeerTube's address comes from the match rather than from the route — it is the one
       * destination here with no address until the search has answered. Its watch page, the same
       * one the Watch on row now offers.
       */
      const url =
        itemId === PEERTUBE_ROW_ID
          ? peerTubeMatch === null
            ? null
            : peerTubeWatchUrl(peerTubeMatch, startSec)
          : shareUrlFor(itemId as ShareTarget, config, target.videoId, startSec);
      if (url === null) return;
      const report = (outcome: 'copied' | 'failed'): void => {
        setShareOutcome(outcome);
        if (shareOutcomeTimer.current !== null) clearTimeout(shareOutcomeTimer.current);
        shareOutcomeTimer.current = setTimeout(() => setShareOutcome(null), 2500);
      };
      navigator.clipboard.writeText(url).then(
        () => report('copied'),
        () => report('failed'),
      );
    },
    [config, live, peerTubeMatch, shareTimestamp, target.videoId, transport.currentSec],
  );

  /**
   * §7's scrub previews. A separate query rather than part of `loadWatch` because it is optional:
   * a video with no storyboard, or an instance that refuses `/sb/…`, must still play. Its failure
   * is swallowed into an empty cue list — there is no empty state for a thumbnail that did not
   * arrive, so reporting it would be reporting it nowhere.
   */
  const storyboardUrl = source?.storyboardVttUrl ?? null;
  const storyboardQuery = useQuery({
    queryKey: ['storyboard', storyboardUrl],
    enabled: storyboardUrl !== null,
    retry: false,
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<readonly StoryboardCue[]> => {
      if (storyboardUrl === null) return [];
      const boot = await getPlatform();
      if (!boot.ok) return [];
      const result = await fetchStoryboardCues(boot.platform, storyboardUrl, {
        spec: details?.storyboard ?? null,
        durationSec: details?.lengthSeconds ?? null,
      });
      return result.ok ? result.value : [];
    },
  });
  const storyboard = storyboardQuery.data ?? [];

  /*
   * Start fetching the engine's chunk now, rather than leaving the effect below to ask for it once
   * the watch payload has landed. The two then overlap: on a cold visit the request runs beside the
   * API round trip, and on every later one it is already in the service worker's precache. This hook
   * only runs while there *is* a session (`PlaybackHost` mounts `PlaybackSession` for a video and
   * nothing else), so no page that cannot play a video asks for it.
   *
   * The rejection is swallowed: this is a head start and not the load. `loadShakaPlayer` drops its
   * cached promise on a failure, so the effect below still asks — and it is the one that reports.
   *
   * Not on the no-MSE arm, which plays the progressive fallback through a plain `src` and never
   * builds a player at all.
   */
  useEffect(() => {
    if (!isMseSupported()) return;
    void loadShakaPlayer().catch(() => undefined);
  }, []);

  useEffect(() => {
    const element = videoRef.current;
    const container = stageRef.current;
    if (source === null || element === null || container === null) return;

    // StrictMode mounts effects twice in development. `cancelled` makes the first pass's player
    // destroy itself instead of racing the second one for the same `<video>` element.
    let cancelled = false;
    let created: PlayerPort | null = null;

    void (async () => {
      try {
        // Wait out the previous player's teardown first — see `playerTeardownRef`.
        await playerTeardownRef.current;
        if (cancelled) return;
        /*
         * The engine is its own chunk (`player/loadShakaPlayer.ts`), so the first player of the
         * session waits for it here. A load that fails is a build that moved under a tab which has
         * been open across it, and the browser's own message for that is the missing URL — written
         * copy instead, since the `catch` below puts whatever it is handed on the screen.
         */
        const { createShakaPlayer } = await loadShakaPlayer().catch(() => {
          throw new Error(text().watch.page.playerCodeFailed);
        });
        const player = await createShakaPlayer(element, container, undefined, {
          p2p: peerTubeIntegratedUrl !== null,
        });
        if (cancelled) {
          playerTeardownRef.current = swallow(player.destroy());
          return;
        }
        created = player;
        playerRef.current = player;

        // §7 — restore the level and the mute flag *separately*, before anything can play. Doing it
        // here rather than after `load()` means the first frame is already at the chosen volume.
        const stored = readPlayerPrefs();
        player.setVolume(stored.volume);
        // An embed asked to start silent is silent whatever the reader last left; the level is
        // untouched, so unmuting from the bar brings it back the way it always does.
        player.setMuted(options.startMuted === true || stored.muted);

        await player.load(source, preferencesRef.current, startAt ?? undefined);

        // Only now (2026-08-02): an error leaves the screen when a load *succeeds*, not when
        // the next attempt starts — clearing on player creation made every retry blink the error
        // away and then put it back, which reads as two failures.
        setPlayerError(null);
        setPlayerRetrying(false);

        /*
         * §8's autoplay, off by default and a *request* rather than a setting (`settings/model.ts`).
         * After `load()` because there is nothing to play before it, and through the same `play()` the
         * big-play button uses — including its muted retry, which is what turns Chrome's refusal into a
         * silent-but-playing video instead of a frozen first frame with no explanation.
         */
        if ((options.autoplay ?? playbackSettings.autoplay) && !cancelled)
          setPlayAttempt(await player.play());
      } catch (error) {
        if (cancelled) return;
        // R9 — `shaka.util.Error.toString()` dumps the failing *signed* URL. The adapter redacts
        // what it records; anything caught raw here is reduced to its message and nothing else.
        setPlayerError(
          error instanceof Error ? error.message : text().watch.page.playerFailed,
        );
        setPlayerRetrying(false);
      }
    })();

    return () => {
      cancelled = true;
      const player = created;
      created = null;
      if (playerRef.current === player) playerRef.current = null;
      tracksKeyRef.current = '';
      setTracks(EMPTY_TRACKS);
      if (player !== null) playerTeardownRef.current = swallow(player.destroy());
    };
    // `manifestUrl` rather than `source`: a re-parse produces a new object for the same stream, and
    // reloading the player on that would restart playback for no reason. `preferences` is
    // deliberately *not* a dependency — it is read through a ref, because a menu selection must
    // re-`apply()` rather than re-`load()`, and having it here would restart the video on every
    // change of speed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestUrl, playerAttempt, startAt]);

  /*
   * `[]` deps would miss the element: `<video ref={videoRef}>` only exists once `PlayerSurface`
   * mounts, which the portal above gates on `stage.kind === 'player'` — on the hook's own first
   * render that is still `'loading'`, so a mount-once effect finds `videoRef.current` null and never
   * looks again. `stage.kind` is what changes when the element does (mounted, or torn down and
   * rebuilt across a failure-and-retry), so re-running on it is what keeps this attached to whichever
   * element currently exists rather than the one that existed a render ago.
   *
   * Once attached, the *element* itself is the one node `playback/dock.ts` moves rather than React
   * re-mounts while `stage.kind` stays `'player'`, so `loadedmetadata` alone still fires for every
   * later `load()` onto it. `resize` covers a mid-playback change no `loadedmetadata` follows: a
   * reload onto a differently shaped manifest (§8's "integrated" PeerTube leg, or a codec-family
   * reload) never emits a second `loadedmetadata` for the same element.
   */
  useEffect(() => {
    const element = videoRef.current;
    if (element === null) return;
    const readDimensions = (): void => {
      if (element.videoWidth > 0 && element.videoHeight > 0) {
        setVideoDimensions({ width: element.videoWidth, height: element.videoHeight });
      }
    };
    readDimensions();
    element.addEventListener('loadedmetadata', readDimensions);
    element.addEventListener('resize', readDimensions);
    return () => {
      element.removeEventListener('loadedmetadata', readDimensions);
      element.removeEventListener('resize', readDimensions);
    };
  }, [stage.kind]);

  const planDurationSec = stage.kind === 'player' ? stage.durationSec : null;
  /** The video's own length, which is how the YouTube leg tells the video from an ad. */
  const lengthSec = details?.lengthSeconds ?? null;

  /**
   * One sample of the player's own state, on demand.
   *
   * Extracted from the poll below because the *seek* needs it too: the bar paints
   * `transport.currentSec` the moment a drag is released, and a sample up to `POLL_MS` old still
   * describes where the video *was*. Re-sampling closes that window — `video.currentTime` reads back
   * the assigned position synchronously, so by the time this returns the transport already agrees with
   * the seek. See `scrubEnd` for why a stale number there was not merely a flicker.
   */
  const sampleTransport = useCallback(() => {
    const youtube = youtubeRef.current;
    if (youtube !== null) {
      /*
       * The YouTube leg. No sample — before the handshake, or while an ad is on — keeps the last
       * transport rather than zeroing it, which is the position the switch was made at.
       *
       * `ended` is held `false`: the queue's advance and /embed's ending read it, and moving on from
       * YouTube's own end screen is a decision that leg has not been given yet.
       */
      const sample = youtube.sample({ videoId, durationSec: lengthSec });
      if (sample !== null) setTransport(toTransportState({ ...sample, ended: false }, chapters, planDurationSec));
      return;
    }
    const player = playerRef.current;
    if (player === null) return;
    setTransport(toTransportState(player.status(), chapters, planDurationSec));
  }, [chapters, lengthSec, planDurationSec, videoId]);

  useEffect(() => {
    const timer = globalThis.setInterval(() => {
      // Ahead of the player check: the YouTube leg is also offered when Visious's own player failed
      // to mount, and its transport has to move there too.
      sampleTransport();
      const player = playerRef.current;
      if (player === null) return;
      // Tracks only exist after `load()` resolves, and they change again on every reload (R6). The
      // signature keeps this from replacing the list — and re-rendering every menu — four times a
      // second with an equal one.
      const next = player.tracks();
      const signature = tracksSignature(next);
      if (signature !== tracksKeyRef.current) {
        tracksKeyRef.current = signature;
        setTracks(next);
      }

      setPointerIdleMs(Math.max(0, systemClock.nowEpochMs() - lastPointerMsRef.current));
    }, POLL_MS);
    return () => globalThis.clearInterval(timer);
    // `sampleTransport` carries the two things this used to read off `stage`, so the interval is no
    // longer torn down and rebuilt every time the stage object's identity changes.
  }, [sampleTransport]);

  /*
   * §8's watched-state mirror. It reads the same two numbers the transport bar already polls, so it costs
   * no extra sampling — and it is a hook rather than a branch in the poll above because the *decision* is
   * core's (`shouldRecordWatched`) and the once-per-video part is a lifetime concern (`useWatchedRecording`).
   *
   * `durationSec: null` on a live stream rather than the element's `Infinity`, which is what makes the
   * relative arm inapplicable there and leaves the flat 30 seconds. `progressive` counts as playable: it
   * is a real watch at 360p, and §7's no-MSE row is a limitation of the picture, not of the viewing.
   */
  useWatchedRecording({
    videoId,
    positionSec: transport.currentSec,
    durationSec: stage.kind === 'player' ? stage.durationSec : null,
    playable: stage.kind === 'player' || stage.kind === 'progressive',
    // 3e's switch, and it is read *live* rather than once per mount unlike the playback defaults: turning
    // recording off is a privacy decision, so it has to take effect on the video already open.
    mirror: settings.mirrorWatchHistory,
  });

  /*
   * 4b's local playhead, off the same two numbers. Deliberately a second hook rather than a branch in
   * `useWatchedRecording`: that one fires **once** per video and never again, while this one writes
   * every five seconds and has to flush on the way out. One shared threshold, two different lifetimes.
   *
   * `enabled` is read *live* like the mirror and unlike the playback defaults, and for the same reason —
   * turning off "remember where I stopped" is a privacy decision, so it must apply to the video that is
   * open rather than to the next one. Note the asymmetry with `resumeSec` above, which is read once:
   * turning the setting *on* mid-video starts recording immediately and does not retroactively seek.
   */
  useWatchProgressRecording({
    videoId,
    positionSec: transport.currentSec,
    durationSec: stage.kind === 'player' ? stage.durationSec : null,
    /*
     * **A live stream is excluded**, which is the same rule `stageForPlan` applies to a start position
     * and it has to be applied here too rather than relying on that: a position that is never restored
     * is still a position that would be *stored*, and a feed row would then offer "Resume at 3:12:44" on
     * a 24/7 broadcast whose seekable window has moved on. A DVR plan is not excluded — it has a real
     * duration and a stable position, which is exactly what distinguishes it from `live`.
     *
     * `progressive` is listed for the same reason 3d's mirror lists it — a 360p watch is a watch — and it
     * is worth knowing that neither of them can actually fire there: the no-MSE arm has no `PlayerPort`,
     * so the poll never produces a position and `currentSec` stays 0. Feeding a plain `<video>`'s
     * `timeupdate` into the transport would fix both at once and belongs to neither slice.
     */
    playable: (stage.kind === 'player' && !stage.live) || stage.kind === 'progressive',
    playing: transport.playing,
    enabled: settings.resumePlayback,
  });

  // Fullscreen is owned by the document, not by us: Escape and the browser's own control both exit
  // it without telling anyone, so the flag is read back from the event rather than assumed.
  useEffect(() => {
    const onChange = (): void => {
      setFullscreen(document.fullscreenElement === stageRef.current);
      // The bar teleports to the other end of the screen without the pointer moving at all, so
      // whatever it was over a moment ago it is not over now. The one transition that produces no
      // boundary event of its own.
      setPointerOverChrome(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const applyPreferences = useCallback((next: PlaybackPreferences) => {
    setPreferences(next);
    preferencesRef.current = next;
    if (next.subtitle.kind === 'lang') lastSubtitleCodeRef.current = next.subtitle.code;
    // §7 persists the speed; quality/audio/subtitles are per-manifest and stay per-session.
    const stored = readPlayerPrefs();
    writePlayerPrefs({ ...stored, playbackRate: next.playbackRate });
    void playerRef.current?.apply(next);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = stageRef.current;
    if (container === null) return;
    if (document.fullscreenElement === container) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      // Rejects when the call is not user-initiated; that is a policy decision, not a failure worth
      // putting on screen.
      void container.requestFullscreen().catch(() => undefined);
    }
  }, []);

  /**
   * A press on the bar's chapter name. Two acts in the order they have to happen: leave fullscreen,
   * because the list the reader is asking for lives on the page the picture is covering, and then
   * ask the panel to scroll.
   *
   * The flag is raised **after** the exit resolves rather than beside it. `exitFullscreen` restores
   * the document's own scroll position as it returns, so a scroll sent first is undone a frame
   * later and the reader is left looking at wherever the page was before they went fullscreen.
   */
  const [revealChapter, setRevealChapter] = useState(false);
  const revealChapters = useCallback(() => {
    const show = (): void => {
      setLayout((previous) => {
        if (previous.chapters) return previous;
        const next = { ...previous, chapters: true };
        writeWatchLayout(next);
        return next;
      });
      setRevealChapter(true);
    };

    if (document.fullscreenElement !== stageRef.current || stageRef.current === null) {
      show();
      return;
    }
    void document.exitFullscreen().then(show, show);
  }, []);

  /**
   * Say on screen that a skip happened, and by how much in total.
   *
   * Called *beside* the command rather than from inside `runCommand`, and that is the one design
   * decision here: the dispatcher is deliberately blind to which device asked, while the answer this
   * draws is not — a finger gets the disc, the bar's buttons and their keyboard twins get the toast,
   * and an OS scrub arriving at the same dispatcher gets neither, because nobody is looking at this
   * window.
   *
   * Guarded on `readyState`, not on there being a player: the mount effect sets `playerRef` *before*
   * `load()`, so after a failed load — routine on this instance, which answered `500` for the DASH
   * manifest through the whole of one verification run — there is a port whose `seek()` is a no-op on
   * an element with no media. A control that answered "10 s" there would be inventing the one thing
   * this feature exists to report. `HAVE_NOTHING` is exactly that state, and nothing else is.
   *
   * **Two timers, not one.** The lifetime is a hold and then a fade, because the disc has to
   * stay put across a run of quick presses instead of flashing: the element is keyed on the run's start,
   * so it survives every press, and the fade is a class the hold timer *adds* — the one way to start an
   * animation on an element React is keeping. A press during the fade takes the class back off and both
   * timers restart, which is what "just stay there until hidden again" is.
   */
  const noteSkip = useCallback((seconds: number, origin: 'gesture' | 'control') => {
    const player = playerRef.current;
    if (player === null || player.status().readyState < 1) return;
    const run = accumulateSkip(skipRunRef.current, { seconds, timeMs: systemClock.nowEpochMs() });
    if (run === null) return;

    skipRunRef.current = run;
    setSkipFeedback({ run, origin, leaving: false });
    if (skipTimerRef.current !== null) globalThis.clearTimeout(skipTimerRef.current);
    if (skipFadeTimerRef.current !== null) globalThis.clearTimeout(skipFadeTimerRef.current);
    skipFadeTimerRef.current = globalThis.setTimeout(() => {
      skipFadeTimerRef.current = null;
      setSkipFeedback((previous) => (previous === null ? null : { ...previous, leaving: true }));
    }, SKIP_FEEDBACK_MS - SKIP_FEEDBACK_EXIT_MS);
    skipTimerRef.current = globalThis.setTimeout(() => {
      skipTimerRef.current = null;
      skipRunRef.current = null;
      setSkipFeedback(null);
    }, SKIP_FEEDBACK_MS);
  }, []);

  useEffect(
    () => () => {
      if (skipTimerRef.current !== null) globalThis.clearTimeout(skipTimerRef.current);
      if (skipFadeTimerRef.current !== null) globalThis.clearTimeout(skipFadeTimerRef.current);
    },
    [],
  );

  /**
   * One dispatcher for every input device. The keymap, the tap gesture and the on-screen buttons
   * all produce a `PlayerCommand`, so there is exactly one place where a control's *meaning* turns
   * into an effect — which is what stops the keyboard and the buttons drifting apart.
   */
  const runCommand = useCallback(
    (command: PlayerCommand) => {
      /*
       * The YouTube leg takes the transport's own commands — play, pause, the seeks, the volume — so a
       * chapter, a timestamp, a key or the OS media controls act on the embed. Before the bridge they
       * reached the paused player under it, and a play there started a second, hidden audio track.
       * With no sample to read (no handshake yet, or an ad on) the relative ones have nothing to be
       * relative to and do nothing. The rest — captions, speed, a frame step, fullscreen — has no
       * counterpart in the message API worth having and falls through as before.
       */
      const youtube = youtubeRef.current;
      if (youtube !== null) {
        const sample = youtube.sample({ videoId, durationSec: lengthSec });
        switch (command.kind) {
          case 'toggle-play':
            if (sample !== null) youtube.send({ kind: sample.paused ? 'play' : 'pause' });
            return;
          case 'play':
          case 'pause':
            youtube.send({ kind: command.kind });
            return;
          case 'seek-to':
            youtube.send({ kind: 'seek', timeSec: command.seconds });
            return;
          case 'seek-by':
            if (sample !== null) youtube.send({ kind: 'seek', timeSec: sample.currentTimeSec + command.seconds });
            return;
          case 'seek-fraction':
            if (sample !== null && sample.durationSec > 0) {
              youtube.send({ kind: 'seek', timeSec: sample.durationSec * command.fraction });
            }
            return;
          case 'volume-by':
            if (sample !== null) youtube.send({ kind: 'volume', level: sample.volume + command.delta });
            return;
          case 'toggle-mute':
            if (sample !== null) youtube.send({ kind: 'mute', muted: !sample.muted });
            return;
          default:
            break;
        }
      }

      const player = playerRef.current;
      if (player === null) return;
      const status = player.status();

      switch (command.kind) {
        case 'toggle-play':
          if (status.paused) void player.play().then(setPlayAttempt);
          else player.pause();
          return;

        /*
         * 4c's one-way arms. `play` does not check `status.paused` first, on purpose: the OS asked for
         * playback and `PlayerPort.play()` on a playing element is a no-op that still reports which of
         * §7's three autoplay outcomes happened — whereas skipping the call would drop a
         * `muted-fallback` on the floor exactly when the reader cannot see the notice.
         */
        case 'play':
          void player.play().then(setPlayAttempt);
          return;

        case 'pause':
          player.pause();
          return;

        case 'seek-by':
          player.seek(Math.max(0, status.currentTimeSec + command.seconds));
          return;

        case 'seek-to':
          player.seek(Math.max(0, command.seconds));
          return;

        case 'seek-fraction': {
          const duration = Number.isFinite(status.durationSec) ? status.durationSec : 0;
          if (duration > 0) player.seek(duration * command.fraction);
          return;
        }

        /*
         * Pause first. §7 had the keys decline while playing, which is indistinguishable from
         * the feature not being wired — and the request behind a frame step is to *look* at a frame,
         * so playing is a state to leave rather than a reason to refuse.
         */
        case 'frame-step':
          if (!status.paused) player.pause();
          player.seek(Math.max(0, status.currentTimeSec + command.frames * frameStepSeconds(tracks.video)));
          return;

        case 'volume-by':
          player.setVolume(Math.min(1, Math.max(0, status.volume + command.delta)));
          persistVolume(player);
          return;

        case 'toggle-mute':
          player.setMuted(!status.muted);
          persistVolume(player);
          return;

        case 'toggle-captions':
          applyPreferences(
            withCaptionsToggled(preferencesRef.current, source?.subtitles ?? [], lastSubtitleCodeRef.current),
          );
          return;

        case 'toggle-fullscreen':
          toggleFullscreen();
          return;

        case 'rate-step':
          applyPreferences({
            ...preferencesRef.current,
            playbackRate: stepPlaybackRate(preferencesRef.current.playbackRate, command.steps),
          });
          return;
      }
    },
    [applyPreferences, lengthSec, source, toggleFullscreen, tracks.video, videoId],
  );

  /*
   * §10 Phase 4's Media Session (4c) — the lock screen, the notification, the car head unit and the
   * hardware media keys.
   *
   * Three things about this call are the slice's whole architecture, and all three are cheap to get wrong:
   *
   * - **It is the third reader of the transport poll**, not a fourth timer. `transport.durationSec` rather
   *   than `stage.durationSec` on purpose: `toTransportState` already prefers the element's own duration
   *   over the plan's, and §2 has `lengthSeconds` lying on real videos while the decoder cannot. Zero
   *   means "not known yet", which core reads as *no position state* — the same answer live gets.
   * - **`transport: null` for the no-MSE arm.** There is no `PlayerPort` there, so `currentSec` is
   *   permanently 0, and publishing that would tell the OS a playing video is parked at the start. The
   *   browser is reading that element correctly on its own; metadata is all we can honestly add.
   * - **`runCommand`, not a handler of its own.** An OS button and a keypress arrive at the same
   *   dispatcher, which is what stops the two drifting — and why `play`/`pause` had to enter core's
   *   vocabulary rather than being folded onto the toggle (`playback/mediaSession.ts`).
   */
  const nowPlaying = useMemo(
    () =>
      details === null || (stage.kind !== 'player' && stage.kind !== 'progressive')
        ? null
        : mediaSessionMetadataFor(details),
    [details, stage.kind],
  );

  useMediaSession({
    metadata: nowPlaying,
    transport:
      stage.kind === 'player'
        ? {
            positionSec: transport.currentSec,
            durationSec: transport.durationSec,
            playbackRate: transport.playbackRate,
            playing: transport.playing,
          }
        : null,
    command: runCommand,
  });

  const handlers: WatchHandlers = useMemo(
    () => ({
      togglePlay: () => runCommand({ kind: 'toggle-play' }),
      pause: () => runCommand({ kind: 'pause' }),
      // Through the dispatcher since 4c rather than straight at the port: a chapter, a description
      // timestamp, a comment's timecode and an OS scrub are the same absolute seek, and this was the one
      // control reaching past the one place a control's meaning turns into an effect.
      seekTo: (seconds: number) => runCommand({ kind: 'seek-to', seconds }),
      seekBy: (deltaSec: number) => {
        noteSkip(deltaSec, 'control');
        runCommand({ kind: 'seek-by', seconds: deltaSec });
      },
      toggleMute: () => runCommand({ kind: 'toggle-mute' }),
      setVolume: (level: number) => {
        // The YouTube leg's slider is the corner player's. Raising the level un-mutes, as it does here.
        const youtube = youtubeRef.current;
        if (youtube !== null) {
          youtube.send({ kind: 'volume', level });
          if (level > 0 && youtube.sample({ videoId, durationSec: lengthSec })?.muted === true) {
            youtube.send({ kind: 'mute', muted: false });
          }
          return;
        }
        const player = playerRef.current;
        if (player === null) return;
        player.setVolume(level);
        persistVolume(player);
        /*
         * Re-sample, for `scrubEnd`'s reason and with the same measurement behind it: this input is
         * *controlled*, and its value comes from a transport sampled at 4 Hz, so during a drag React
         * kept re-writing a level up to 250 ms old into the element the pointer was moving. The knob
         * stuttered backwards under the thumb and the slider felt like it was resisting — dragging felt
         * sluggish. `video.volume` reads back synchronously, exactly like
         * `currentTime`, so one sample here makes the element and the state agree within the gesture.
         */
        sampleTransport();
      },
      toggleDescription: () => setDescriptionExpanded((previous) => !previous),
      command: runCommand,

      /*
       * One entry point for two listeners: the player surface's own `onKeyDown`, and a
       * document-level one for the keys that have to work while focus is elsewhere on the page
       * (`useDocumentPlayerKeys`). So `focusInsidePlayer` is *measured* here rather than asserted — it
       * gates `↑`/`↓`, and answering `true` for a press that landed on the comments would take page
       * scrolling away to change the volume.
       */
      key: (event: KeyEventLike, target: KeyContext['target']): boolean => {
        // `/embed/ID?disablekb=1`. Refused here rather than at each listener, since both of them
        // come through this one function.
        if (!keyboardEnabled) return false;
        const stage = stageRef.current;
        const command = keyToCommand(event, {
          focusInsidePlayer: stage !== null && stage.contains(document.activeElement),
          target,
        });
        if (command === null) return false;
        lastPointerMsRef.current = systemClock.nowEpochMs();
        setPointerIdleMs(0);
        runCommand(command);
        // `←`/`→` and `J`/`L` *are* the bar's skip buttons under another name, so they raise the same
        // toast. Not `seek-fraction` or `frame-step`: a jump to 40 % is not a distance, and a frame
        // step is a still picture moving one frame — neither is a run anyone counts.
        if (command.kind === 'seek-by') noteSkip(command.seconds, 'control');
        return true;
      },

      surfaceDown: (clientX: number, clientY: number, button: number) => {
        if (button !== PRIMARY_POINTER_BUTTON) return;
        pointerDownRef.current = { x: clientX, y: clientY };
      },

      surfaceUp: (
        clientX: number,
        clientY: number,
        xFraction: number,
        pointer: TapInput['pointer'],
        button: number,
      ) => {
        // Both halves check it, so a right press neither starts a gesture nor finishes one. Checking
        // only the release would leave the press recorded, and the next real tap would measure its
        // movement from wherever the menu was opened.
        if (button !== PRIMARY_POINTER_BUTTON) return;
        const down = pointerDownRef.current;
        pointerDownRef.current = null;
        // No `pointerdown` recorded means the gesture started somewhere else — a drag that ended
        // over the video. `Infinity` makes `decideTap` reject it as movement, which is what it is.
        const movedPx =
          down === null ? Number.POSITIVE_INFINITY : Math.hypot(clientX - down.x, clientY - down.y);

        const { state, action } = decideTap(tapStateRef.current, {
          timeMs: systemClock.nowEpochMs(),
          xFraction,
          movedPx,
          pointer,
        });
        tapStateRef.current = state;
        lastPointerMsRef.current = systemClock.nowEpochMs();
        setPointerIdleMs(0);
        switch (action.kind) {
          case 'toggle-play':
            runCommand({ kind: 'toggle-play' });
            return;
          case 'skip':
            noteSkip(action.seconds, 'gesture');
            runCommand({ kind: 'seek-by', seconds: action.seconds });
            return;
          case 'toggle-fullscreen':
            toggleFullscreen();
            return;
          case 'reveal':
          case 'none':
            return;
        }
      },

      /**
       * A pick closes the menu; the audio menu's **stable-volume switch does not**. It is the
       * Share dropdown's timestamp row, exactly: a modifier of what the rows above it mean rather
       * than one of them, so a menu that vanished on the flip would cost a reopen to see what it did
       * — and here what it did is a *row label* changing, which nobody would see at all.
       */
      selectMenuItem: (menu: MenuId, itemId: string) => {
        const current = preferencesRef.current;
        switch (menu) {
          case 'quality':
            applyPreferences(withQualitySelection(current, itemId, tracks.video));
            break;
          case 'audio':
            /*
             * A caption row folded into this menu below `md`. Routed on the prefix rather than
             * on which menu is open, because the two lists address their rows by *language code* — `en`
             * is a dub in one and a caption track in the other, and without this a reader choosing
             * English subtitles would have switched the audio track to English instead.
             */
            if (itemId.startsWith(SUBTITLE_ROW_PREFIX)) {
              applyPreferences(
                withSubtitleSelection(current, itemId.slice(SUBTITLE_ROW_PREFIX.length)),
              );
              break;
            }
            if (itemId === STABLE_VOLUME_ID) {
              // The menu's switch *is* the settings toggle, so it persists where a language pick is
              // a per-video choice. The sync module sees the write and pushes as usual (harmless —
              // the setting itself is local-only, the push only carries the mapped fields).
              const on = current.audio.drc !== true;
              applyPreferences(withStableVolume(current, on));
              writeSettings({ useStableVolume: on });
              // Returns rather than breaks: the one item that leaves the menu open.
              return;
            }
            applyPreferences(withAudioSelection(current, itemId));
            break;
          case 'subtitles':
            applyPreferences(withSubtitleSelection(current, itemId));
            break;
          case 'speed':
            applyPreferences(withRateSelection(current, itemId));
            break;
        }
        setOpenMenu(null);
      },

      openMenu: setOpenMenu,
      toggleFullscreen,

      toggleTheater: () =>
        setLayout((previous) => {
          const next = { ...previous, theater: !previous.theater };
          writeWatchLayout(next);
          return next;
        }),
      toggleStageCrop: () => setStageCrop((previous) => !previous),
      toggleChapters: () =>
        setLayout((previous) => {
          const next = { ...previous, chapters: !previous.chapters };
          writeWatchLayout(next);
          return next;
        }),
      revealChapters,
      chapterRevealed: () => setRevealChapter(false),
      toggleRecommendations: () =>
        setLayout((previous) => {
          const next = { ...previous, recommendations: !previous.recommendations };
          writeWatchLayout(next);
          return next;
        }),
      toggleComments: () =>
        setLayout((previous) => {
          const next = { ...previous, comments: !previous.comments };
          writeWatchLayout(next);
          return next;
        }),
      togglePlaylist: () =>
        setLayout((previous) => {
          const next = { ...previous, playlist: !previous.playlist };
          writeWatchLayout(next);
          return next;
        }),
      setPlaylistAutoplay: (on: boolean) =>
        setLayout((previous) => {
          const next = { ...previous, playlistAutoplay: on };
          writeWatchLayout(next);
          return next;
        }),
      setQueueAutoplay: (on: boolean) =>
        setLayout((previous) => {
          const next = { ...previous, queueAutoplay: on };
          writeWatchLayout(next);
          return next;
        }),

      /**
       * A drag only *paints*; the seek happens once, on release. Seeking on every `input` event
       * across a chaptered two-hour video issues a seek per pixel, and each one asks the engine to
       * fetch a new segment — the picture stutters through the drag and the buffer is thrown away
       * dozens of times to reach a position the user was only passing through. Arrow keys on the
       * focused slider produce no `pointerdown`, so they still seek immediately: there is no drag
       * to release.
       *
       * The condition is `draggingRef` rather than "is anything painted", and that is the whole
       * repair: the browser sends one more value change *after* the gesture — see `scrubEnd` — and
       * asking the painted value whether a drag is in progress read that echo as a fresh request.
       */
      scrubTo: (seconds: number) => {
        if (!draggingRef.current) {
          playerRef.current?.seek(seconds);
          // No painted position for this arm: the sample *is* the paint, and one that cannot be left
          // behind — a painted value with no gesture to end it would pin the bar and the chrome open.
          sampleTransport();
          return;
        }
        scrubbingRef.current = seconds;
        setScrubbingSec(seconds);
      },
      scrubStart: () => {
        const at = playerRef.current?.status().currentTimeSec ?? 0;
        draggingRef.current = true;
        scrubbingRef.current = at;
        setScrubbingSec(at);
      },
      /**
       * Commit the drag — and leave nothing stale behind, because that is what the bug was.
       *
       * Clearing the painted position here used to hand the range input back the *polled* playhead,
       * up to `POLL_MS` old and therefore a description of where the video was before the drag. React
       * writes that number into the element while the browser still owns the gesture, and the trailing
       * `change` event then arrives carrying it — measured as a second `currentTime` assignment 8 ms
       * after the first, back to the position the reader had just dragged away from. The seek was
       * never lost; it was undone, which is exactly what "snaps back and keeps playing" looks like.
       * Intermittent because it is a race with React's flush, which is why it took three or four
       * tries to get a seek to stick.
       *
       * So: sample the player *after* seeking it, so what the bar (and the input) go back to is the
       * position that was just asked for. The echo then re-requests the position we are already at,
       * which is a no-op, and `draggingRef` keeps it from being read as a drag either way.
       */
      scrubEnd: () => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        const at = scrubbingRef.current;
        scrubbingRef.current = null;
        if (at !== null) playerRef.current?.seek(at);
        sampleTransport();
        setScrubbingSec(null);
      },
      /**
       * A `pointercancel` is the gesture being *taken away*, not finished, and the two must not commit
       * the same thing. On a touchscreen this is what the page's own scroller does to a swipe that
       * started on the bar — and the engine sets the slider's value from the press point before the
       * first move, so committing would seek to whatever spot the reader happened to touch on their
       * way to scrolling the page. Abandoning is the only reading that matches what they did.
       */
      scrubCancel: () => {
        draggingRef.current = false;
        scrubbingRef.current = null;
        setScrubbingSec(null);
      },
      hoverBar: setHoverFraction,
      pointerActivity: () => {
        lastPointerMsRef.current = systemClock.nowEpochMs();
        setPointerIdleMs(0);
        // A move that reaches the surface is a move that was *not* over the bar — the chrome stops its
        // own so that this stays true. Which also means the flag cannot stick: any pointer movement
        // anywhere on the picture clears it, even if a `pointerleave` never came.
        setPointerOverChrome(false);
      },
      chromeActivity: () => {
        lastPointerMsRef.current = systemClock.nowEpochMs();
        setPointerIdleMs(0);
        setPointerOverChrome(true);
      },
      setFocusInside,

      retry: () => void query.refetch(),
      retryPlayer: () => {
        // The error stays up: it leaves when a load succeeds, and until then the button's
        // busy face is the acknowledgement.
        setPlayerRetrying(true);
        setPlayerAttempt((previous) => previous + 1);
      },
    }),
    [
      applyPreferences,
      keyboardEnabled,
      lengthSec,
      noteSkip,
      query,
      revealChapters,
      runCommand,
      sampleTransport,
      toggleFullscreen,
      tracks.video,
      videoId,
    ],
  );

  // The keymap while focus is elsewhere on the page — the surface's own handler keeps the
  // presses that land inside it, and `keys.ts` documents what this one declines and why.
  useDocumentPlayerKeys(handlers.key, options.minimized !== true);

  /**
   * Fetched only while the section is shown: a reader who keeps comments folded away pays
   * nothing for them, and the first Show is what sends the first request. Still independent of the
   * video request — a shown section does not wait on the 305 KB video payload to be parsed.
   * `live` only decides which sentence an absent list gets — for a shown section the request goes
   * out either way, because "no comments" and "comments off" are the endpoint's answer to give,
   * not ours to assume.
   */
  const comments = useComments(videoId, {
    enabled: options.comments !== false && layout.comments,
    live,
  });

  /*
   * The paged read behind a `list` in the address, which projects that playlist into the queue. Off
   * entirely without a `list`, on /embed, and while the panel is hidden — the run costs a request per
   * ~200 entries the reader moves through, and a hidden panel must not spend them (the comments
   * section's rule, for the same reason).
   */
  const listLoad = useWatchPlaylist({
    playlistId: target.playlistId,
    index: target.index,
    videoId,
    enabled: options.playlist !== false,
    shown: layout.playlist,
  });

  /*
   * The one panel, built from the queue plus that read. There is no choice to make between two lists any
   * more: a `list` in the address *is* the queue, so what the column draws is the queue either way.
   */
  const queue = useQueue();
  const queuePanel = useQueuePanel(listLoad);
  const listPanel = options.playlist === false ? null : queuePanel;
  /*
   * Which list that is, which decides the panel's per-list words, which of the two Autoplay switches it
   * reads and what Clear does. The link is the answer — a queue that is a window on a playlist is a
   * playlist — and the address covers the render before the first page has landed, where the link does
   * not exist yet and the panel is already on screen.
   */
  const listKind: 'playlist' | 'queue' =
    queue.link === null && target.playlistId === null ? 'queue' : 'playlist';

  /*
   * The two acts on the list *as a whole*, resolved to whichever list the panel is drawing. Both live
   * here rather than in `handlers` because `handlers` is memoised above, before the route has said
   * which list this is — a resolution inside that memo would either close over a stale `listKind` or
   * make the memo depend on it and rebuild the whole dispatcher on every list change.
   */
  const setPlaylistAutoplay = handlers.setPlaylistAutoplay;
  const setQueueAutoplay = handlers.setQueueAutoplay;
  const listAutoplay = useMemo(
    (): WatchListAutoplay =>
      listKind === 'queue'
        ? { on: layout.queueAutoplay, set: setQueueAutoplay }
        : { on: layout.playlistAutoplay, set: setPlaylistAutoplay },
    [layout.playlistAutoplay, layout.queueAutoplay, listKind, setPlaylistAutoplay, setQueueAutoplay],
  );

  /*
   * Letting go of the list, which is two acts at once for a list that came out of the address.
   *
   * The queue goes either way, since this tab is the only place the reader's window on the list exists.
   * A **linked** list also has to leave the address, and both halves are needed: `list` left behind and
   * the panel's loader reads the playlist straight back into the queue. Nothing is sent — the record on
   * the instance is untouched and the video keeps playing.
   *
   * The navigation **replaces** rather than pushes: the address is being corrected to describe what the
   * reader is now watching, not moved to a new place, so Back stays the way out of the page they came
   * from instead of walking back into a list they just closed.
   */
  const clearList = useCallback(() => {
    clearQueue();
    if (listKind === 'playlist') {
      navigate(watchWithoutListHref(videoId, target.startSec), { replace: true });
    }
  }, [listKind, target.startSec, videoId]);

  /*
   * Keeping the list, the other act at that end of the control row — and the two directions the link can
   * be crossed in.
   *
   * **Letting go of a playlist** is the same two-part shape `clearList` has above, and for the same
   * reason: the store drops the link and the address drops `list` and `index`, or the loader reads the
   * playlist back in on the next render. It replaces rather than pushes, since the address is being
   * corrected to describe what the reader is now watching.
   *
   * **Taking a playlist up** after the queue has been saved into it goes through the address as well, and
   * that is the whole of the act here: arriving at a `list` route is what makes the panel's loader read
   * the playlist and project it into the queue, so the rows the reader keeps are the playlist's own in the
   * playlist's own order. Linking in the store instead would have to invent a position for every row the
   * save skipped as already present, and those are the playlist's to state.
   */
  const listKeep = useMemo(
    (): WatchListKeep => ({
      unlink: () => {
        unlinkQueue();
        navigate(watchWithoutListHref(videoId, target.startSec), { replace: true });
      },
      linkTo: (playlistId: string, index: number | null) => {
        navigate(watchWithListHref(videoId, target.startSec, playlistId, index), { replace: true });
      },
      signInHref,
    }),
    [signInHref, target.startSec, videoId],
  );

  /*
   * Stepping the **corner player** through the list — the two buttons either side of its Play
   * (`MiniControls.tsx`). Neither navigates: a corner player that walked the whole app to another page
   * would be the least minimized thing on screen. The watch page's own panel keeps its links, since
   * there a step *is* where the reader wants to go.
   *
   * One mechanism for both lists, because there is one list: the corner's target **is** the queue's
   * cursor (`queue/model.ts`'s `queueWatchTarget`), so moving the cursor is the whole act and the
   * retained session it was playing is retired on the next render (`playback/model.ts`).
   */
  const minimized = options.minimized === true;
  const listStep = useMemo((): WatchListStep => {
    const stepTo = (row: WatchPlaylistRow | null): (() => void) | null =>
      row === null ? null : () => playQueueEntry(row.key);
    return { previous: stepTo(listPanel?.previous ?? null), next: stepTo(listPanel?.next ?? null) };
  }, [listPanel]);

  /*
   * The advance, the route↔cursor reconciliation that makes a queue row's own link enough, and the
   * arrival that discards a queue the route has left behind. All three are the queue's, so all three are
   * in its own file — and since every list the panel draws is the queue, this is the only advance on the
   * page. It moves the cursor rather than navigating off it, which is what lets it keep working in the
   * corner player where a navigating advance had to stand down.
   *
   * The `list` goes in rather than being folded into a flag: it is what says whether this route and this
   * queue are about the same list. /embed is the one caller that must not touch a queue at all.
   *
   * `autoplay` is the list's own switch, already resolved, and it reaches only the advance in there — the
   * other two rules are about the address bar and the list agreeing about what is playing, which is true
   * whether or not the list plays on by itself.
   */
  useQueueAdvance({
    videoId,
    playlistId: target.playlistId,
    ended: transport.ended,
    enabled: options.playlist !== false,
    onRoute: !minimized,
    autoplay: listAutoplay.on,
  });

  /*
   * SponsorBlock. `playable` names a VOD in the real player: live has no fixed timeline for a
   * segment range to mean anything on, and the no-MSE arm has no port, so its position is stuck at
   * zero and an engine fed from it would decide about second 0 forever. The settings come from the
   * live subscription — like the mirror switch and unlike the playback defaults — because turning
   * the integration off must stop the skipping on the video already open.
   */
  const sponsor = useSponsorBlock({
    videoId,
    settings: settings.sponsorBlock,
    playable: stage.kind === 'player' && !live,
    chapters,
    positionSec: transport.currentSec,
    playing: transport.playing,
    scrubbing: scrubbingSec !== null,
    command: runCommand,
  });

  // Return YouTube Dislike: one lookup for the video on screen, gated by its own settings switch
  // exactly like SponsorBlock's — enabling it sends the plain video id to a third-party service.
  const dislikeCount = useReturnYoutubeDislike(videoId, settings.returnYoutubeDislike);
  const dislikeCounter = dislikeCounterView(dislikeCount);

  /*
   * The player escape hatch (2026-08-03): replace Visious's own player with an embedded
   * YouTube video, or with a matched PeerTube one (embedded, or streamed into this same Shaka
   * player — `source`'s own `peerTubeIntegratedUrl` branch above). `player.md`'s reasoning for the
   * corner player applies here too, the other way round: pausing Visious's own transport is what
   * keeps a hidden `<video>` from bleeding audio under the iframe, *and* is what makes the corner
   * player release the session if the reader navigates away mid-switch (retention needs `playing`,
   * and a switched-away player never is) — no extra guard needed for "never minimized while
   * switched", it falls out of the existing rule for free.
   */
  /** Where the YouTube leg has got to, or `null` when it is not the one playing (or has no sample). */
  const youtubePositionSec = useCallback(
    (): number | null => youtubeRef.current?.sample({ videoId, durationSec: lengthSec })?.currentTimeSec ?? null,
    [lengthSec, videoId],
  );

  const switchToExternal = useCallback((provider: 'youtube' | 'peertube', url: string) => {
    playerRef.current?.pause();
    setExternal({ provider, url });
    setActiveSource(provider);
  }, []);

  const switchBackToVisious = useCallback(() => {
    /*
     * **Every leg goes, not only the one on screen.** An embedded leg and the integrated one can be
     * set at the same time — the iframe wins the stage and the integrated manifest sits paused
     * underneath it — so a switch back that cleared only the leg it happened to branch on left the
     * other one alive. The iframe is the half that bites: it is its own document with its own audio,
     * and a YouTube embed that survived a hop through the integrated PeerTube leg went on playing
     * behind Visious's own player, with nothing on screen to say where the sound was coming from.
     */
    const youtubeSec = youtubePositionSec();
    setExternal(null);
    setActiveSource('visious');
    if (peerTubeIntegratedUrl !== null) {
      // The integrated leg is a different manifest in the *same* player — switching back is a
      // reload, not a resume, and it carries over wherever that leg's playhead actually reached
      // (or YouTube's, when that was covering it).
      const capturedSec = youtubeSec ?? playerRef.current?.status().currentTimeSec ?? 0;
      setSwitchSeek(capturedSec);
      setPeerTubeIntegratedUrl(null);
      return;
    }
    // The embedded legs never touched Visious's own player beyond pausing it — it is still sitting
    // exactly where it was, so resuming is instant and needs no reload. The YouTube leg reports how
    // far it got, so the player picks up from there rather than from where it was hidden.
    if (youtubeSec !== null) playerRef.current?.seek(youtubeSec);
    void playerRef.current?.play().then(setPlayAttempt);
  }, [peerTubeIntegratedUrl, youtubePositionSec]);

  /**
   * The PeerTube leg's search (2026-08-03): kicked off when a dropdown carrying a PeerTube row
   * *opens* (`peerTubeMenuOnOpen` — Watch on, and Share link since 2026-08-06), not by the row's own
   * click, so by the time a reader can press it the answer is usually already in. A hit is cached in
   * `peerTubeMatch` rather than acted on immediately: the rows stay disabled until it resolves, and
   * the actual switch happens only on a deliberate click (`switchPlayerTo`), which is what lets the
   * same match serve either mode — and all three rows — without a second search.
   */
  const runPeerTubeSearch = useCallback(async () => {
    if (video === null || details === null) return;
    setPeerTubeSwitchState({ kind: 'searching' });

    const boot = await getPlatform();
    if (!boot.ok) {
      setPeerTubeSwitchState({ kind: 'unavailable' });
      return;
    }
    const searchResult = await fetchPeerTubeSearchResults(boot.platform, settings.peerTube, video.title);
    if (!searchResult.ok) {
      setPeerTubeSwitchState({ kind: 'unavailable' });
      return;
    }
    const match = pickPeerTubeMatch(searchResult.value, video.title, details.lengthSeconds);
    if (match === null) {
      setPeerTubeSwitchState({ kind: 'unavailable' });
      return;
    }
    setPeerTubeMatch(match);
    setPeerTubeSwitchState({ kind: 'found', instanceHost: match.instanceHost });
  }, [details, settings.peerTube, video]);

  /**
   * Both dropdowns that carry a PeerTube row open through here — Watch on and Share
   * link — and the `idle` guard is what makes that one search rather than three: whichever opens
   * first starts it, the others read the answer it left behind. A video with no title or no length
   * is not searchable at all, and leaving the state `idle` there is what lets the first open *after*
   * the details arrive still start one.
   */
  const peerTubeMenuOnOpen = useCallback(() => {
    if (!settings.peerTube.enabled || !peerTubeSearchable) return;
    if (peerTubeSwitchState.kind !== 'idle') return;
    void runPeerTubeSearch();
  }, [peerTubeSearchable, peerTubeSwitchState.kind, runPeerTubeSearch, settings.peerTube.enabled]);

  const share: ShareBinding = useMemo(
    () => ({
      menu: shareMenu,
      feedback: shareOutcome === null ? null : shareOutcomeText(shareOutcome),
      onSelect: shareSelect,
      onOpen: peerTubeMenuOnOpen,
    }),
    [peerTubeMenuOnOpen, shareMenu, shareOutcome, shareSelect],
  );

  /**
   * The "integrated" mode's one extra request — the HLS master, only fetched once a click commits.
   * Back to `searching` for its duration (reusing the row's own busy state) since this, unlike the
   * embedded leg, is not instant; a success replaces the row entirely (`activeSource` flips to
   * `peertube`, so the Switch player section renders the way back in its place) and a failure needs
   * the found match re-checked, so it reports the same "no video found" a fresh miss would.
   *
   * A success puts the state **back to `found`**: the row it belongs to is hidden while
   * PeerTube is the one driving, so nothing showed that it had been left mid-search — and switching
   * back to Visious brought the row out again wearing a spinner and disabled, with no second search
   * left to finish it. The match is the same one the borrowed `searching` interrupted, so there is
   * nothing to look up again.
   */
  const switchToPeerTubeIntegrated = useCallback(
    async (match: PeerTubeSearchResult, capturedSec: number) => {
      setPeerTubeSwitchState({ kind: 'searching' });
      const boot = await getPlatform();
      if (!boot.ok) {
        setPeerTubeSwitchState({ kind: 'unavailable' });
        return;
      }
      const detailsResult = await fetchPeerTubeHlsPlaylistUrl(boot.platform, match.instanceHost, match.uuid);
      if (!detailsResult.ok || detailsResult.value === null) {
        setPeerTubeSwitchState({ kind: 'unavailable' });
        return;
      }
      setSwitchSeek(capturedSec);
      setPeerTubeIntegratedUrl(detailsResult.value);
      // The leg this replaces, if it was an embedded one. `Stage` draws an iframe ahead of every
      // other arm, so leaving it up would cover the leg the reader just asked for — and go on
      // playing under it.
      setExternal(null);
      setActiveSource('peertube');
      setPeerTubeSwitchState({ kind: 'found', instanceHost: match.instanceHost });
    },
    [],
  );

  const switchPlayerTo = useCallback(
    (destination: ActivePlayerSource) => {
      if (destination === 'visious') {
        switchBackToVisious();
        return;
      }
      if (destination === 'youtube') {
        const capturedSec = playerRef.current?.status().currentTimeSec ?? transport.currentSec;
        // The page's own origin turns the embed's message API on — see `youtubeEmbedUrl`.
        const url = youtubeEmbedUrl(videoId, capturedSec, globalThis.location.origin);
        if (url !== null) switchToExternal('youtube', url);
        return;
      }
      if (peerTubeSwitchState.kind !== 'found' || peerTubeMatch === null) return;
      const capturedSec = youtubePositionSec() ?? playerRef.current?.status().currentTimeSec ?? transport.currentSec;
      if (settings.peerTube.mode === 'embedded') {
        const url = peerTubeEmbedUrl(peerTubeMatch, capturedSec);
        if (url !== null) switchToExternal('peertube', url);
        return;
      }
      void switchToPeerTubeIntegrated(peerTubeMatch, capturedSec);
    },
    [
      peerTubeMatch,
      peerTubeSwitchState.kind,
      settings.peerTube.mode,
      switchBackToVisious,
      switchToExternal,
      switchToPeerTubeIntegrated,
      transport.currentSec,
      videoId,
      youtubePositionSec,
    ],
  );

  /**
   * One handler for a menu whose rows do three different things, sorted by the id the model gave
   * them: a swap acts here, the timestamp toggles what the next link carries, and a link row is
   * already being followed by the browser by the time this runs.
   */
  const watchOnSelect = useCallback(
    (itemId: string) => {
      const row = watchOnRowOf(itemId);
      if (row === null) return;
      if (row.kind === 'timestamp') {
        setJumpOutTimestamp((value) => !value);
        return;
      }
      if (row.kind === 'switch') switchPlayerTo(row.target);
    },
    [switchPlayerTo],
  );

  const watchOn: WatchOnBinding = useMemo(
    () => ({
      menu: watchOnMenu,
      onSelect: watchOnSelect,
      onOpen: peerTubeMenuOnOpen,
      active: activeSource !== 'visious',
    }),
    [activeSource, peerTubeMenuOnOpen, watchOnMenu, watchOnSelect],
  );

  const chrome: WatchChrome = useMemo(() => {
    // One row per language; the stable-volume switch is the menu's own bottom section, reading
    // `preferences.audio.drc` — stamped from the setting at mount, flipped by the switch itself.
    const audio = audioMenu(tracks.audio, preferences);
    // Built from the plan, not the engine: captions are sideloaded, so the list exists before the
    // manifest is even parsed and the menu need not wait for a load.
    const subtitles = live ? null : subtitleMenu(source?.subtitles ?? [], preferences);
    const quality = qualityMenu(tracks.video, preferences);
    const speed = speedMenu(preferences);
    /*
     * Below `md` the two become one control: the audio rows with the caption rows under a
     * heading of their own, behind the audio icon. `mergedAudioMenu` keeps whichever exists when only
     * one does — so a one-language video still shows its captions, under their own icon and id — and
     * the `subtitles` slot going `null` is what stops the bar drawing that control twice.
     */
    const audioControl = narrowViewport ? mergedAudioMenu(audio, subtitles) : audio;

    return {
      visible: chromeVisible({
        playing: transport.playing,
        pointerIdleMs,
        focusInside,
        pointerOverChrome,
        menuOpen: openMenu !== null,
        scrubbing: scrubbingSec !== null,
      }),
      fullscreen,
      openMenu,
      hoverFraction,
      scrubbingSec,
      narrow: narrowViewport,
      quality: narrowViewport ? menuWithoutValue(quality) : quality,
      speed: narrowViewport ? menuWithoutValue(speed) : speed,
      audio: narrowViewport ? menuWithoutValue(audioControl) : audioControl,
      subtitles: narrowViewport ? null : subtitles,
    };
  }, [
    focusInside,
    fullscreen,
    hoverFraction,
    live,
    narrowViewport,
    openMenu,
    pointerIdleMs,
    pointerOverChrome,
    preferences,
    scrubbingSec,
    source,
    tracks.audio,
    tracks.video,
    transport.playing,
  ]);

  return {
    videoId,
    stage,
    video,
    degraded: query.data?.ok === false && query.data.degraded,
    rail,
    watchOn,
    share,
    saveToPlaylist: { signInHref },
    transport,
    videoPortrait: videoDimensions !== null && videoDimensions.height > videoDimensions.width,
    videoAspectRatio:
      videoDimensions === null
        ? null
        : `${String(videoDimensions.width)} / ${String(videoDimensions.height)}`,
    stageAspectRatio: String(stageBox.aspect),
    stageCrop: stageBox.crop,
    stageCropAvailable: stageBox.cropAvailable,
    chrome,
    storyboard,
    playAttempt,
    playerError,
    // Busy while a re-*fetch* is in flight — never on the first load, which is the loading stage.
    retrying: query.isFetching && !query.isPending,
    playerRetrying,
    descriptionExpanded,
    layout,
    wideViewport,
    comments,
    playlist: listPanel,
    playlistKind: listKind,
    listStep,
    listAutoplay,
    clearList,
    listKeep,
    sponsor,
    revealChapter,
    dislikeCountText: dislikeCounter.text,
    dislikeCountTitle: dislikeCounter.title,
    external,
    externalFrameRef: setExternalFrame,
    skip:
      skipFeedback === null
        ? null
        : skipFeedbackView(skipFeedback.run, skipFeedback.origin, skipFeedback.leaving),
    handlers,
    videoRef,
    stageRef,
  };
}

/** A settled promise whatever happened — for a teardown whose failure has no reader. */
function swallow(promise: Promise<void>): Promise<void> {
  return promise.catch(() => undefined);
}

/**
 * Below the `md` step, subscribed the same way and for the same reason: the *bar* renders different
 * things there, not the same things smaller. The stylesheet's own `max-width: 719px` — one below the
 * breakpoint, which is how every `max-width` query in this app is written (the token test accepts both
 * spellings of a boundary).
 *
 * Exported for `MiniControls`, which needs the same boundary for the same reason: below it the
 * minimized player is a bar on a themed surface rather than a picture with chrome on it, and both
 * the buttons' colours and the swipe directions differ there.
 */
export function useNarrowViewport(): boolean {
  const query = `(max-width: ${String(breakpoints.md - 1)}px)`;
  const [narrow, setNarrow] = useState(() => globalThis.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = globalThis.matchMedia?.(query);
    if (media === undefined) return;
    const onChange = (): void => setNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

/**
 * `matchMedia` over the stylesheet's own wide breakpoint, subscribed — a resize across it re-renders,
 * because on the narrow side the page renders *different* elements, not the same ones elsewhere.
 * `false` where `matchMedia` does not exist, which only a non-browser test runner is.
 */
function useWideViewport(): boolean {
  const query = `(min-width: ${String(breakpoints.wide)}px)`;
  const [wide, setWide] = useState(() => globalThis.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = globalThis.matchMedia?.(query);
    if (media === undefined) return;
    const onChange = (): void => setWide(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return wide;
}
